import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as THREE from 'three';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { getSnapPointsFromScene } from '../../../shared/snap/getSnapPointsFromScene';
import { selectAllFloorPlans } from '../../../redux/floorPlanSlice';
import { selectAllWires } from '../../../redux/wireSlice';
import { findPanelExitPoints, findBellTrafoForBranch, updateBellTrafoPosition, updatePanelTitles, updateWireTitleNumbers } from '../../../modules/wiring';
import { updateFeeder, selectFeeders } from '../../../redux/simpleSupplySlice';
import { findBundleByPanelExitIndex } from '../../../utils/wireBundling';
import { GizmoEditCommand } from '../../../modules/undoRedo';
import { MoveCommand } from '../../../modules/undoRedo';
import { WireGeometryEditCommand } from '../../../modules/undoRedo';
import {
    rayIntersection2D, circumcenter,
    updateGizmoScale, updateAllGizmosScale,
    rerenderMtextSprite, updateMtextGizmos,
    updateArcGizmos, updateEllipseGizmos, updateCircleGizmos, updateGizmos
} from '../../../utils/gizmo/gizmoUpdateHelpers';

export function useGizmo(scene, camera, domElement) {
    const dispatch = useDispatch();
    const floorPlans = useSelector(selectAllFloorPlans);
    const wires = useSelector(selectAllWires);
    const feeders = useSelector(selectFeeders); // ✅ Redux feeders - wire-feeder eşleştirmesi için
    const orthoMode = useSelector((state) => state.mods.orthoMode);
    const gridMode = useSelector((state) => state.mods.gridMode); // ✅ Grid mode state'i

    const selectedGizmo = useRef(null);
    const isMoving = useRef(false);
    const originalPosition = useRef(new THREE.Vector3());
    const originalEdgePositions = useRef({ start: new THREE.Vector3(), end: new THREE.Vector3() }); // Midpoint için kenarın uçları
    const originalSockets = useRef([]); // ✅ Hareket ettirilecek socketler
    const originalVertexData = useRef(null); // Vertex gizmo için tüm etkilenen pozisyonlar
    const connectedPanelExitPoints = useRef(null); // ✅ Panel çıkış noktaları
    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane();
    const offset = new THREE.Vector3();
    const planeIntersect = new THREE.Vector3();
    const snapMarkerRef = useRef(null);
    const snappedPointRef = useRef(null);
    const lastSnappedExitPoint = useRef(null); // ✅ Track last valid exit point for swapping
    const lastSnappedExitIndex = useRef(-1); // ✅ Track last valid exit INDEX for swap chain
    const initialExitPoint = useRef(null); // ✅ Track initial exit point for offset calculation
    const currentPanelId = useRef(null); // ✅ Track current panel ID
    const currentWireObj = useRef(null); // ✅ Track current wire object
    const lastValidMidpointPerp = useRef(null); // ✅ Panel-exit kısıtı için son geçerli midpoint perpVector
    // Snap point cache — getSnapPointsFromScene her mousemove'da çağrılırsa O(N²) olabiliyor.
    // 500ms TTL ile cache'le; sahnede yeni nesne sürüklemeden önce zaten taze hesaplanmış olur.
    const snapCacheRef = useRef({ time: 0, points: null });
    // Drag-süresince obje id→ref cache. Drag başında lazy build edilir, mousemove'larda
    // O(1) lookup, mouseup'ta temizlenir. Mousemove'da ~20+ scene.traverse (find-by-id)
    // çağrısı vardı; her biri tüm sahneyi taradığı için drag latency büyüktü.
    const dragIdMapRef = useRef(null);
    const currentTrafo = useRef(null); // ✅ Track current bell trafo
    const originalWireMarkerData = useRef(null); // ✅ Wire marker orijinal geometrileri (ESC ile restore için)
    const originalConnectedWires = useRef([]); // ✅ Bağlı lighting ve fixture wire'ların orijinal vertex pozisyonları (ESC ile restore için)
    const isSimpleDrawing = useRef(false); // ✅ Basit çizim nesnesi mi? (line, polyline, etc.)
    const originalAllVertices = useRef([]); // ✅ Tüm vertex pozisyonları (ESC restore için)
    const isCircleDrawing = useRef(false); // ✅ Circle çizim nesnesi mi?
    const originalCircleData = useRef(null); // ✅ { center, radius, vertices[] } - Circle ESC restore için
    const isArcDrawing = useRef(false); // ✅ Arc çizim nesnesi mi?
    const originalArcData = useRef(null); // ✅ { center, radius, startAngle, endAngle, vertices[] }
    const isEllipseDrawing = useRef(false); // ✅ Ellipse çizim nesnesi mi?
    const originalEllipseData = useRef(null); // ✅ { center, radiusX, radiusY, rotation, vertices[] }
    const isBlockDragging = useRef(false); // ✅ Block sürükleme aktif mi?
    const blockObjRef = useRef(null); // ✅ Sürüklenen THREE.Group
    const originalBlockPos = useRef(new THREE.Vector3()); // ✅ Orijinal pozisyon (normal veya OCS)
    const blockIsOCS = useRef(false); // ✅ matrixAutoUpdate === false mi?
    const isMtextMoving = useRef(false); // ✅ MTEXT taşıma aktif mi?
    const isMtextResizing = useRef(false); // ✅ MTEXT resize aktif mi?
    const mtextObjRef = useRef(null); // ✅ Sürüklenen MTEXT nesnesi
    const originalMtextPos = useRef(new THREE.Vector3()); // ✅ Orijinal pozisyon
    const originalMtextScale = useRef(new THREE.Vector3()); // ✅ Orijinal scale
    const mtextCornerIdx = useRef(-1); // ✅ Sürüklenen köşe indexi
    const mtextFixedCorner = useRef(new THREE.Vector3()); // ✅ Karşı köşe (sabit)
    // ── Wire geometry undo/redo ──────────────────────────────────────────────
    const wireBeforeVertices = useRef([]);       // Sürükleme başlamadan önce ana wire'ın tüm vertex'leri
    const wireBeforeId = useRef(null);           // Ana wire ID
    const allPanelWiresBeforeState = useRef([]); // [{wireId, vertices}] — panel çıkış swap için tüm bağlı wire'lar

    // Tesisat (wire) nesnesi mi? Wire-specific userData alanlarını kontrol eder
    const isWireObject = (obj) => {
        if (!obj) return true; // null ise wire gibi davran (güvenli taraf)
        const ud = obj.userData;
        if (!ud) return false;
        return !!(
            ud.function ||       // Tüm wire'larda var: simpleSocket, simpleLighting, wireBell, wireLightingFixture, etc.
            ud.panelID ||        // Panele bağlı wire
            ud.sourceId ||       // Kaynak panel
            ud.isWire ||         // Açıkça wire olarak işaretlenmiş
            ud.branchID ||       // Bell wire dalı
            ud.wireMarkerIds ||  // Wire marker'ları var
            ud.sockets ||        // Socket listesi
            ud.buatIds           // Buat listesi
        );
    };

    useEffect(() => {
        if (!scene || !camera || !domElement) return;

        const getMouse = (event) => {
            const rect = domElement.getBoundingClientRect();
            return {
                x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
                y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
            };
        };

        const getSnapDistance = (pixels = 10) => {
            const v1 = new THREE.Vector3(-1, 0, 0).unproject(camera);
            const v2 = new THREE.Vector3(-1 + (pixels / domElement.clientWidth) * 2, 0, 0).unproject(camera);
            return v1.distanceTo(v2);
        };

        const onClick = (event) => {
            const mouse = getMouse(event);
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(scene.children, true);
            // ✅ wireMarker'ları hariç tut
            const gizmo = intersects.find(i =>
                i.object.name.startsWith("__gizmo") &&
                i.object.userData?.role !== 'wireMarker'
            );
            if (!isMoving.current && gizmo) {
                selectedGizmo.current = gizmo.object;
                originalPosition.current.copy(gizmo.object.position);
                connectedPanelExitPoints.current = null; // Reset
                lastValidMidpointPerp.current = null; // Yeni drag → panel-exit son geçerli perp sıfırlansın
                dragIdMapRef.current = null; // Yeni drag → id cache sıfırlansın, ilk lookup'ta yeniden build edilir

                // ===== CIRCLE DRAWING DETECTION =====
                const gizmoUserType = gizmo.object.userData.type;
                if (gizmoUserType === 'circleCenter' || gizmoUserType === 'circleQuarter') {
                    const circleId = gizmo.object.userData.circleId;
                    let circleObj = null;
                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === circleId) circleObj = obj;
                    });

                    if (circleObj) {
                        isCircleDrawing.current = true;
                        const pos = circleObj.geometry.attributes.position;
                        originalCircleData.current = {
                            center: { ...circleObj.userData.center },
                            radius: circleObj.userData.radius,
                            vertices: []
                        };
                        for (let i = 0; i < pos.count; i++) {
                            originalCircleData.current.vertices.push(
                                new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                            );
                        }

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return; // Skip all wire/line logic
                    }
                }
                // ===== END CIRCLE DETECTION =====

                // ===== ARC DRAWING DETECTION =====
                if (gizmoUserType === 'arcCenter' || gizmoUserType === 'arcStart' || gizmoUserType === 'arcEnd' || gizmoUserType === 'arcMidpoint') {
                    const arcId = gizmo.object.userData.arcId;
                    let arcObj = null;
                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === arcId) arcObj = obj;
                    });

                    if (arcObj) {
                        isArcDrawing.current = true;
                        const pos = arcObj.geometry.attributes.position;
                        originalArcData.current = {
                            center: { ...arcObj.userData.center },
                            radius: arcObj.userData.radius,
                            startAngle: arcObj.userData.startAngle,
                            endAngle: arcObj.userData.endAngle,
                            vertices: []
                        };
                        for (let i = 0; i < pos.count; i++) {
                            originalArcData.current.vertices.push(
                                new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                            );
                        }

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return;
                    }
                }
                // ===== END ARC DETECTION =====

                // ===== ELLIPSE DRAWING DETECTION =====
                if (gizmoUserType === 'ellipseCenter' || gizmoUserType === 'ellipseQuarter') {
                    const ellipseId = gizmo.object.userData.ellipseId;
                    let ellipseObj = null;
                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === ellipseId) ellipseObj = obj;
                    });

                    if (ellipseObj) {
                        isEllipseDrawing.current = true;
                        const pos = ellipseObj.geometry.attributes.position;
                        originalEllipseData.current = {
                            center: { ...ellipseObj.userData.center },
                            radiusX: ellipseObj.userData.radiusX,
                            radiusY: ellipseObj.userData.radiusY,
                            rotation: ellipseObj.userData.rotation || 0,
                            vertices: []
                        };
                        for (let i = 0; i < pos.count; i++) {
                            originalEllipseData.current.vertices.push(
                                new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                            );
                        }

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return;
                    }
                }
                // ===== END ELLIPSE DETECTION =====

                // ===== BLOCK BASEPOINT DETECTION =====
                if (gizmoUserType === 'blockBasePoint') {
                    const blockId = gizmo.object.userData.blockId;
                    let blockObj = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id === blockId && obj.userData?.type === 'block') blockObj = obj;
                    });

                    if (blockObj) {
                        isBlockDragging.current = true;
                        blockObjRef.current = blockObj;
                        blockIsOCS.current = (blockObj.matrixAutoUpdate === false);

                        // Orijinal pozisyonu kaydet
                        if (blockIsOCS.current) {
                            const m = blockObj.matrix.elements;
                            originalBlockPos.current.set(m[12], m[13], m[14]);
                        } else {
                            originalBlockPos.current.copy(blockObj.position);
                        }

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return;
                    }
                }
                // ===== END BLOCK DETECTION =====

                // ===== MTEXT CENTER DETECTION (MOVE) =====
                if (gizmoUserType === 'mtextCenter') {
                    const mtextId = gizmo.object.userData.mtextId;
                    let mtextObj = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id === mtextId && obj.userData?.type === 'mtext') mtextObj = obj;
                    });

                    if (mtextObj) {
                        isMtextMoving.current = true;
                        mtextObjRef.current = mtextObj;
                        originalMtextPos.current.copy(mtextObj.position);
                        originalMtextScale.current.copy(mtextObj.scale);

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return;
                    }
                }
                // ===== END MTEXT CENTER DETECTION =====

                // ===== MTEXT CORNER DETECTION (RESIZE) =====
                if (gizmoUserType === 'mtextCorner') {
                    const mtextId = gizmo.object.userData.mtextId;
                    const cornerIndex = gizmo.object.userData.cornerIndex;
                    let mtextObj = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id === mtextId && obj.userData?.type === 'mtext') mtextObj = obj;
                    });

                    if (mtextObj) {
                        isMtextResizing.current = true;
                        mtextObjRef.current = mtextObj;
                        mtextCornerIdx.current = cornerIndex;
                        originalMtextPos.current.copy(mtextObj.position);
                        originalMtextScale.current.copy(mtextObj.scale);

                        // Karşı köşeyi hesapla (i+2)%4
                        const oppositeIdx = (cornerIndex + 2) % 4;
                        if (mtextObj.isSprite) {
                            const p = mtextObj.position;
                            const sx = mtextObj.scale.x, sy = mtextObj.scale.y;
                            const corners = [
                                new THREE.Vector3(p.x, p.y, 0),
                                new THREE.Vector3(p.x + sx, p.y, 0),
                                new THREE.Vector3(p.x + sx, p.y - sy, 0),
                                new THREE.Vector3(p.x, p.y - sy, 0),
                            ];
                            mtextFixedCorner.current.copy(corners[oppositeIdx]);
                        } else {
                            mtextObj.updateWorldMatrix(true, false);
                            const box = new THREE.Box3().setFromObject(mtextObj);
                            const corners = [
                                new THREE.Vector3(box.min.x, box.max.y, 0),
                                new THREE.Vector3(box.max.x, box.max.y, 0),
                                new THREE.Vector3(box.max.x, box.min.y, 0),
                                new THREE.Vector3(box.min.x, box.min.y, 0),
                            ];
                            mtextFixedCorner.current.copy(corners[oppositeIdx]);
                        }

                        isMoving.current = true;
                        plane.setFromNormalAndCoplanarPoint(
                            camera.getWorldDirection(new THREE.Vector3()),
                            selectedGizmo.current.position
                        );
                        raycaster.ray.intersectPlane(plane, planeIntersect);
                        offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                        return;
                    }
                }
                // ===== END MTEXT CORNER DETECTION =====

                // ✅ Check if wire is connected to a panel
                const { lineId } = gizmo.object.userData;
                const wire = wires.find(w => w.id === lineId);

                // Find wire object in scene to check userData directly (fallback for non-redux wires)
                let wireObj = null;
                scene.traverse(obj => {
                    if (obj.isLine && obj.userData.id === lineId) {
                        wireObj = obj;
                    }
                });
                currentWireObj.current = wireObj; // ✅ Store it

                // Bundle rep: V0 ve son vertex doğrudan sürüklenemez
                if (wireObj?.userData?.isBundleRep && gizmo.object.userData.type === 'vertex') {
                    const vIdx = gizmo.object.userData.vertexIndex;
                    const posCount = wireObj.geometry?.attributes?.position?.count ?? 0;
                    if (vIdx === 0 || (posCount >= 1 && vIdx === posCount - 1)) {
                        selectedGizmo.current = null;
                        return;
                    }
                }

                // ===== SIMPLE DRAWING OBJECT DETECTION =====
                // Wire/tesisat değilse → basit çizim nesnesi (user-drawn line, DXF line, etc.)
                isSimpleDrawing.current = wireObj && wireObj.isLine && !isWireObject(wireObj);

                if (isSimpleDrawing.current) {
                    // Store all vertex positions for ESC restore
                    const pos = wireObj.geometry.attributes.position;
                    originalAllVertices.current = [];
                    for (let i = 0; i < pos.count; i++) {
                        originalAllVertices.current.push(
                            new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                        );
                    }
                    // Skip wire-specific logic (panel, sockets, collinear chain, etc.)
                    isMoving.current = true;
                    plane.setFromNormalAndCoplanarPoint(
                        camera.getWorldDirection(new THREE.Vector3()),
                        selectedGizmo.current.position
                    );
                    raycaster.ray.intersectPlane(plane, planeIntersect);
                    offset.copy(planeIntersect).sub(selectedGizmo.current.position);
                    return; // Early return, skip all wire logic
                }
                // ===== END SIMPLE DRAWING DETECTION =====

                // ── Wire geometry undo/redo: drag başlangıcında tam vertex snapshot'ı al ──
                if (wireObj && wireObj.geometry?.attributes?.position) {
                    const _pos = wireObj.geometry.attributes.position;
                    const _bv = [];
                    for (let _i = 0; _i < _pos.count; _i++) {
                        _bv.push({ x: _pos.getX(_i), y: _pos.getY(_i), z: _pos.getZ(_i) });
                    }
                    wireBeforeVertices.current = _bv;
                    wireBeforeId.current = lineId;
                } else {
                    wireBeforeVertices.current = [];
                    wireBeforeId.current = null;
                }

                // 🛠️ PATCH: If sockets is missing but lightSwitches exists, populate it (Runtime Fix)
                if (wireObj && !wireObj.userData.sockets && wireObj.userData.lightSwitches) {
                    wireObj.userData.sockets = wireObj.userData.lightSwitches;
                    wireObj.userData.socketIdsMap = wireObj.userData.lightSwitches.reduce((acc, id) => {
                        acc[id] = true;
                        return acc;
                    }, {});
                }

                // ✅ Find Bell Trafo if exists - ONLY for bell wires
                const isBellWire = wireObj && (
                    wireObj.userData.layer === 'bell' ||
                    wireObj.userData.id?.startsWith('wireBell') ||
                    wireObj.userData.type === 'wireBell'
                );
                if (isBellWire && wireObj.userData.branchID) {
                    const trafo = findBellTrafoForBranch(scene, wireObj.userData.branchID);
                    // ✅ Verify ownership: Only accept trafo if IDs explicitly match
                    if (trafo && trafo.userData.branchID === wireObj.userData.branchID) {
                        currentTrafo.current = trafo;
                    } else {
                        currentTrafo.current = null;
                    }
                } else {
                    currentTrafo.current = null;
                }

                const panelId = wire?.sourceId || wire?.userData?.panelID || wireObj?.userData?.sourceId || wireObj?.userData?.panelID;

                if (panelId) {
                    currentPanelId.current = panelId; // ✅ Store panel ID
                    let panel = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id === panelId) {
                            const type = obj.userData?.type;
                            const name = obj.name;
                            if (type === 'panel' || type === 'mainPanel' || type === 'ElectricalPanel' || name === 'panel') {
                                panel = obj;
                            }
                        }
                    });

                    if (panel) {

                        const exitPoints = findPanelExitPoints(panel);
                        connectedPanelExitPoints.current = exitPoints.map(ep => {
                            const wp = new THREE.Vector3();
                            ep.getWorldPosition(wp);
                            return wp;
                        });

                        // ✅ Log exit points if clicking on first vertex or first edge
                        const { type, vertexIndex, edgeIndex } = gizmo.object.userData;


                        // ✅ Initialize lastSnappedExitPoint
                        let wireStartPos = null;
                        scene.traverse(o => {
                            if (o.userData.id === lineId && o.isLine) {
                                const pos = o.geometry.attributes.position;
                                wireStartPos = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                            }
                        });

                        if (wireStartPos) {
                            let closest = null;
                            let closestIndex = -1;
                            let minD = Infinity;
                            connectedPanelExitPoints.current.forEach((ep, idx) => {
                                const d = ep.distanceTo(wireStartPos);
                                if (d < minD) { minD = d; closest = ep; closestIndex = idx; }
                            });
                            // Relaxed threshold for bell trafos which might be offset
                            if (minD < 5.0) {
                                lastSnappedExitPoint.current = closest.clone();
                                lastSnappedExitIndex.current = closestIndex; // ✅ Track index for swap chain
                                initialExitPoint.current = closest.clone();
                            } else {
                                // Fallback: if connected to panel, assume closest is correct even if far (e.g. large trafo)
                                lastSnappedExitPoint.current = closest ? closest.clone() : null;
                                lastSnappedExitIndex.current = closest ? closestIndex : -1; // ✅ Track index for swap chain
                                initialExitPoint.current = closest ? closest.clone() : null;
                            }
                        }

                        // ── Wire geometry undo/redo: panel'e bağlı tüm wire'ların before-state'ini kaydet ──
                        allPanelWiresBeforeState.current = [];
                        scene.traverse(_pwObj => {
                            if (!_pwObj.isLine) return;
                            const _wPanelId = _pwObj.userData?.panelID || _pwObj.userData?.sourceId;
                            if (_wPanelId !== panelId) return;
                            const _p = _pwObj.geometry?.attributes?.position;
                            if (!_p || _p.count < 1) return;
                            const _verts = [];
                            for (let _i = 0; _i < _p.count; _i++) {
                                _verts.push({ x: _p.getX(_i), y: _p.getY(_i), z: _p.getZ(_i) });
                            }
                            allPanelWiresBeforeState.current.push({ wireId: _pwObj.userData.id, vertices: _verts });
                        });
                    }
                } else {
                    currentPanelId.current = null;
                }

                // ✅ Midpoint için kenarın iki ucunun orijinal pozisyonlarını sakla
                if (gizmo.object.userData.type === 'midpoint') {
                    const { lineId, edgeIndex } = gizmo.object.userData;
                    let wireObj = null;

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === lineId) {
                            wireObj = obj;
                            const pos = obj.geometry.attributes.position;

                            // Helper to get vector for edge i
                            const getEdgeVec = (i) => {
                                const p1 = new THREE.Vector3().fromBufferAttribute(pos, i);
                                const p2 = new THREE.Vector3().fromBufferAttribute(pos, i + 1);
                                return p2.sub(p1).normalize();
                            };

                            // Scan Left for collinear edges
                            let startEdgeIndex = edgeIndex;
                            while (startEdgeIndex > 0) {
                                const vLeft = getEdgeVec(startEdgeIndex - 1);
                                const vCurr = getEdgeVec(startEdgeIndex);
                                if (vLeft.angleTo(vCurr) < 0.1) {
                                    startEdgeIndex--;
                                } else {
                                    break;
                                }
                            }

                            // Scan Right for collinear edges
                            let endEdgeIndex = edgeIndex;
                            while (endEdgeIndex < pos.count - 2) {
                                const vRight = getEdgeVec(endEdgeIndex + 1);
                                const vCurr = getEdgeVec(endEdgeIndex);
                                if (vCurr.angleTo(vRight) < 0.1) {
                                    endEdgeIndex++;
                                } else {
                                    break;
                                }
                            }

                            // Store the range of collinear edges
                            originalEdgePositions.current.collinearStartIdx = startEdgeIndex;
                            originalEdgePositions.current.collinearEndIdx = endEdgeIndex;

                            // Store block endpoints
                            originalEdgePositions.current.blockStart = new THREE.Vector3(
                                pos.getX(startEdgeIndex),
                                pos.getY(startEdgeIndex),
                                pos.getZ(startEdgeIndex)
                            );
                            originalEdgePositions.current.blockEnd = new THREE.Vector3(
                                pos.getX(endEdgeIndex + 1),
                                pos.getY(endEdgeIndex + 1),
                                pos.getZ(endEdgeIndex + 1)
                            );

                            // Store all vertices in the block
                            originalEdgePositions.current.blockVertices = [];
                            for (let i = startEdgeIndex; i <= endEdgeIndex + 1; i++) {
                                originalEdgePositions.current.blockVertices.push(
                                    new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                                );
                            }

                            // Kenarın başlangıç ve bitiş noktalarını sakla
                            originalEdgePositions.current.start.set(
                                pos.getX(edgeIndex),
                                pos.getY(edgeIndex),
                                pos.getZ(edgeIndex)
                            );
                            originalEdgePositions.current.end.set(
                                pos.getX(edgeIndex + 1),
                                pos.getY(edgeIndex + 1),
                                pos.getZ(edgeIndex + 1)
                            );
                        }
                    });

                    // ✅ Bu kenar üzerindeki socketleri, buatları, machine'leri, bell'leri ve wire markerları bul
                    originalSockets.current = [];
                    originalConnectedWires.current = []; // ✅ Reset connected wires
                    if (wireObj) {
                        const socketIds = wireObj.userData.sockets || [];
                        const socketIdsMap = wireObj.userData.socketIdsMap || {};
                        const buatIds = wireObj.userData.buatIds || [];
                        const buatIdsMap = wireObj.userData.buatIdsMap || {}; // ✅ Buat ID Map'i al
                        const wireMarkerIds = wireObj.userData.wireMarkerIds || [];
                        const wireMarkerIdsMap = wireObj.userData.wireMarkerIdsMap || {}; // ✅ Wire Marker ID Map'i al
                        const machineIds = wireObj.userData.machineIds || []; // ✅ Machine ID listesi
                        const machineIdsMap = wireObj.userData.machineIdsMap || {}; // ✅ Machine ID Map'i al
                        const bellIds = wireObj.userData.bellIds || []; // ✅ Bell ID listesi
                        const bellIdsMap = wireObj.userData.bellIdsMap || {}; // ✅ Bell ID Map'i al

                        // Iterate over ALL collinear edges
                        const startIdx = originalEdgePositions.current.collinearStartIdx;
                        const endIdx = originalEdgePositions.current.collinearEndIdx;
                        const pos = wireObj.geometry.attributes.position;

                        for (let i = startIdx; i <= endIdx; i++) {
                            const start = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
                            const end = new THREE.Vector3(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1));
                            const line3 = new THREE.Line3(start, end);

                            if (socketIds.length > 0 || Object.keys(socketIdsMap).length > 0 ||
                                buatIds.length > 0 || Object.keys(buatIdsMap).length > 0 ||
                                wireMarkerIds.length > 0 || Object.keys(wireMarkerIdsMap).length > 0 ||
                                machineIds.length > 0 || Object.keys(machineIdsMap).length > 0 ||
                                bellIds.length > 0 || Object.keys(bellIdsMap).length > 0) {
                                scene.traverse(child => {
                                    const childId = child.userData.id;

                                    // Hedef obje mi? (Socket, Buat, Machine, Bell veya WireMarker)
                                    const isTarget = childId && (
                                        socketIdsMap[childId] ||
                                        socketIds.includes(childId) ||
                                        buatIdsMap[childId] || // ✅ Map ile kontrol
                                        buatIds.includes(childId) ||
                                        wireMarkerIdsMap[childId] || // ✅ Wire Marker kontrolü
                                        wireMarkerIds.includes(childId) ||
                                        machineIdsMap[childId] || // ✅ Machine kontrolü
                                        machineIds.includes(childId) ||
                                        bellIdsMap[childId] || // ✅ Bell kontrolü
                                        bellIds.includes(childId)
                                    );

                                    if (isTarget) {
                                        // Obje bu kenar üzerinde mi?
                                        const closestPoint = new THREE.Vector3();
                                        line3.closestPointToPoint(child.position, true, closestPoint);
                                        const dist = child.position.distanceTo(closestPoint);

                                        // ⚠️ Threshold artırıldı: 0.1 -> 0.5 (Buat'lar 0.2 mesafede olabiliyor)
                                        if (dist < 0.5) {
                                            // Check if already added (could happen if close to vertex)
                                            const alreadyAdded = originalSockets.current.some(s => s.object === child);
                                            if (!alreadyAdded) {
                                                // ✅ Wire marker ise işaretle (her zaman ortada kalmalı)
                                                const isWireMarker = child.userData?.role === 'wireMarker';

                                                // Parametrik pozisyonu hesapla
                                                const edgeVector = new THREE.Vector3().subVectors(end, start);
                                                const edgeLength = edgeVector.length();
                                                const toPoint = new THREE.Vector3().subVectors(closestPoint, start);
                                                const t = edgeLength > 0 ? toPoint.dot(edgeVector) / (edgeLength * edgeLength) : 0;

                                                originalSockets.current.push({
                                                    object: child,
                                                    originalPosition: child.position.clone(),
                                                    t: isWireMarker ? 0.5 : t, // ✅ Wire marker her zaman ortada (t=0.5)
                                                    isWireMarker: isWireMarker, // ✅ Wire marker flag
                                                    edgeIndex: i, // ✅ Hangi kenar üzerinde (midpoint için)
                                                    lineId: lineId // ✅ Hangi wire üzerinde
                                                });
                                            }
                                        }
                                    }
                                });
                            }
                        }
                    }
                }

                // ✅ Bağlı lighting ve fixture wire'ların orijinal pozisyonlarını kaydet (midpoint için)
                originalSockets.current.forEach(socketData => {
                    const obj = socketData.object;
                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle')) {
                        // ✅ Fixture wire'lar
                        if (obj.userData?.fixtureWireMapIds && obj.userData.fixtureWireMapIds.length > 0) {
                            obj.userData.fixtureWireMapIds.forEach(fixtureWireId => {
                                scene.traverse(fixtureObj => {
                                    if (fixtureObj.isLine &&
                                        fixtureObj.userData?.id === fixtureWireId &&
                                        fixtureObj.userData?.function === 'wireLightingFixture') {

                                        const fixturePos = fixtureObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: fixtureWireId,
                                            type: 'fixture',
                                            vertex0: new THREE.Vector3(
                                                fixturePos.getX(0),
                                                fixturePos.getY(0),
                                                fixturePos.getZ(0)
                                            )
                                        });
                                    }
                                });
                            });
                        }

                        // ✅ Lighting wire'lar
                        if (obj.userData?.lightingWireMapIds && obj.userData.lightingWireMapIds.length > 0) {
                            obj.userData.lightingWireMapIds.forEach(lightingWireId => {
                                scene.traverse(lightingObj => {
                                    if (lightingObj.isLine &&
                                        lightingObj.userData?.id === lightingWireId &&
                                        (lightingObj.userData?.function === 'wireLighting' ||
                                            lightingObj.userData?.function === 'simpleLighting')) {

                                        const lightingPos = lightingObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: lightingWireId,
                                            type: 'lighting',
                                            vertex0: new THREE.Vector3(
                                                lightingPos.getX(0),
                                                lightingPos.getY(0),
                                                lightingPos.getZ(0)
                                            ),
                                            vertex1: new THREE.Vector3(
                                                lightingPos.getX(1),
                                                lightingPos.getY(1),
                                                lightingPos.getZ(1)
                                            )
                                        });
                                    }
                                });
                            });
                        }
                    }

                    // ✅ Socket wire'lar (socketBuat için)
                    if (obj.userData?.type === 'socketBuat') {
                        if (obj.userData?.socketWireMapIds && obj.userData.socketWireMapIds.length > 0) {
                            obj.userData.socketWireMapIds.forEach(socketWireId => {
                                scene.traverse(socketObj => {
                                    if (socketObj.isLine &&
                                        socketObj.userData?.id === socketWireId &&
                                        (socketObj.userData?.function === 'socket' ||
                                            socketObj.userData?.function === 'simpleSocket')) {

                                        const socketPos = socketObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: socketWireId,
                                            type: 'socket',
                                            vertex0: new THREE.Vector3(
                                                socketPos.getX(0),
                                                socketPos.getY(0),
                                                socketPos.getZ(0)
                                            ),
                                            vertex1: new THREE.Vector3(
                                                socketPos.getX(1),
                                                socketPos.getY(1),
                                                socketPos.getZ(1)
                                            )
                                        });
                                    }
                                });
                            });
                        }
                    }
                });

                // ✅ Vertex gizmo için tüm etkilenen noktaların orijinal pozisyonlarını sakla
                if (gizmo.object.userData.type === 'vertex') {
                    const { lineId, vertexIndex } = gizmo.object.userData;

                    let wireObj = null;
                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === lineId) {
                            wireObj = obj;
                            const pos = obj.geometry.attributes.position;
                            const data = {
                                vertex: new THREE.Vector3(pos.getX(vertexIndex), pos.getY(vertexIndex), pos.getZ(vertexIndex)),
                                leftNeighbor: null,
                                leftLeftNeighbor: null,
                                rightNeighbor: null,
                                rightRightNeighbor: null
                            };

                            // Sol komşu (vertexIndex - 1)
                            if (vertexIndex > 0) {
                                data.leftNeighbor = new THREE.Vector3(
                                    pos.getX(vertexIndex - 1),
                                    pos.getY(vertexIndex - 1),
                                    pos.getZ(vertexIndex - 1)
                                );
                            }
                            // Sol-sol komşu (vertexIndex - 2)
                            if (vertexIndex > 1) {
                                data.leftLeftNeighbor = new THREE.Vector3(
                                    pos.getX(vertexIndex - 2),
                                    pos.getY(vertexIndex - 2),
                                    pos.getZ(vertexIndex - 2)
                                );
                            }
                            // Sağ komşu (vertexIndex + 1)
                            if (vertexIndex < pos.count - 1) {
                                data.rightNeighbor = new THREE.Vector3(
                                    pos.getX(vertexIndex + 1),
                                    pos.getY(vertexIndex + 1),
                                    pos.getZ(vertexIndex + 1)
                                );
                            }

                            // Sağ-sağ komşu (vertexIndex + 2)
                            if (vertexIndex + 1 < pos.count - 1) {
                                data.rightRightNeighbor = new THREE.Vector3(
                                    pos.getX(vertexIndex + 2),
                                    pos.getY(vertexIndex + 2),
                                    pos.getZ(vertexIndex + 2)
                                );
                            }

                            originalVertexData.current = data;

                            // ✅ Detect Collinear Chains for Vertex Dragging
                            // We need to detect Left Chain and Right Chain independently.

                            const getEdgeVec = (i) => {
                                const p1 = new THREE.Vector3().fromBufferAttribute(pos, i);
                                const p2 = new THREE.Vector3().fromBufferAttribute(pos, i + 1);
                                return p2.sub(p1).normalize();
                            };

                            // 1. Detect Left Chain (ending at vertexIndex)
                            // A chain exists if edge (vertexIndex-1) is collinear with (vertexIndex-2)
                            let leftChainStartEdge = -1;
                            if (vertexIndex > 1) {
                                const v1 = getEdgeVec(vertexIndex - 1); // Edge ending at vertex
                                const v2 = getEdgeVec(vertexIndex - 2); // Previous edge
                                if (v1.angleTo(v2) < 0.1) {
                                    leftChainStartEdge = vertexIndex - 1;
                                    // Expand Left
                                    while (leftChainStartEdge > 0) {
                                        const vCurr = getEdgeVec(leftChainStartEdge);
                                        const vPrev = getEdgeVec(leftChainStartEdge - 1);
                                        if (vCurr.angleTo(vPrev) < 0.1) {
                                            leftChainStartEdge--;
                                        } else {
                                            break;
                                        }
                                    }
                                }
                            }

                            if (leftChainStartEdge !== -1) {
                                originalVertexData.current.leftCollinearChain = {
                                    startEdgeIdx: leftChainStartEdge,
                                    endEdgeIdx: vertexIndex - 1,
                                    vertices: []
                                };
                                // Store vertices: from start of first edge to end of last edge (vertexIndex)
                                for (let i = leftChainStartEdge; i <= vertexIndex; i++) {
                                    originalVertexData.current.leftCollinearChain.vertices.push({
                                        index: i,
                                        pos: new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                                    });
                                }
                            }

                            // 2. Detect Right Chain (starting at vertexIndex)
                            // A chain exists if edge (vertexIndex) is collinear with (vertexIndex+1)
                            let rightChainEndEdge = -1;
                            if (vertexIndex < pos.count - 2) {
                                const v1 = getEdgeVec(vertexIndex); // Edge starting at vertex
                                const v2 = getEdgeVec(vertexIndex + 1); // Next edge
                                if (v1.angleTo(v2) < 0.1) {
                                    rightChainEndEdge = vertexIndex + 1;
                                    // Expand Right
                                    while (rightChainEndEdge < pos.count - 2) {
                                        const vCurr = getEdgeVec(rightChainEndEdge);
                                        const vNext = getEdgeVec(rightChainEndEdge + 1);
                                        if (vCurr.angleTo(vNext) < 0.1) {
                                            rightChainEndEdge++;
                                        } else {
                                            break;
                                        }
                                    }
                                }
                            }

                            if (rightChainEndEdge !== -1) {
                                originalVertexData.current.rightCollinearChain = {
                                    startEdgeIdx: vertexIndex,
                                    endEdgeIdx: rightChainEndEdge,
                                    vertices: []
                                };
                                // Store vertices: from vertexIndex to end of last edge
                                for (let i = vertexIndex; i <= rightChainEndEdge + 1; i++) {
                                    originalVertexData.current.rightCollinearChain.vertices.push({
                                        index: i,
                                        pos: new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i))
                                    });
                                }
                            }
                        }
                    });

                    // ✅ Vertex gizmo için etkilenen kenarlardaki komponentleri bul
                    // Vertex sürüklendiğinde SOL ve SAĞ kenarlar etkilenir
                    // UPDATE: If collinear chain exists, we need ALL components on the chain
                    originalSockets.current = [];
                    originalConnectedWires.current = []; // ✅ Reset connected wires
                    if (wireObj) {
                        const socketIds = wireObj.userData.sockets || [];
                        const socketIdsMap = wireObj.userData.socketIdsMap || {};
                        const buatIds = wireObj.userData.buatIds || [];
                        const buatIdsMap = wireObj.userData.buatIdsMap || {};
                        const wireMarkerIds = wireObj.userData.wireMarkerIds || [];
                        const wireMarkerIdsMap = wireObj.userData.wireMarkerIdsMap || {};
                        const machineIds = wireObj.userData.machineIds || [];
                        const machineIdsMap = wireObj.userData.machineIdsMap || {};
                        const bellIds = wireObj.userData.bellIds || [];
                        const bellIdsMap = wireObj.userData.bellIdsMap || {};

                        // Determine range of edges to scan
                        let edgesToScan = [];
                        const pos = wireObj.geometry.attributes.position;

                        // Add Left Chain Edges
                        if (originalVertexData.current && originalVertexData.current.leftCollinearChain) {
                            const c = originalVertexData.current.leftCollinearChain;
                            for (let i = c.startEdgeIdx; i <= c.endEdgeIdx; i++) {
                                edgesToScan.push({
                                    index: i,
                                    start: new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)),
                                    end: new THREE.Vector3(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1))
                                });
                            }
                        } else if (vertexIndex > 0) {
                            // Default Left Neighbor
                            edgesToScan.push({
                                index: vertexIndex - 1,
                                start: new THREE.Vector3(pos.getX(vertexIndex - 1), pos.getY(vertexIndex - 1), pos.getZ(vertexIndex - 1)),
                                end: new THREE.Vector3(pos.getX(vertexIndex), pos.getY(vertexIndex), pos.getZ(vertexIndex))
                            });
                        }

                        // Add Right Chain Edges
                        if (originalVertexData.current && originalVertexData.current.rightCollinearChain) {
                            const c = originalVertexData.current.rightCollinearChain;
                            for (let i = c.startEdgeIdx; i <= c.endEdgeIdx; i++) {
                                // Avoid duplicates if chains overlap (should not happen with current logic)
                                if (!edgesToScan.some(e => e.index === i)) {
                                    edgesToScan.push({
                                        index: i,
                                        start: new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)),
                                        end: new THREE.Vector3(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1))
                                    });
                                }
                            }
                        } else if (vertexIndex < pos.count - 1) {
                            // Default Right Neighbor
                            if (!edgesToScan.some(e => e.index === vertexIndex)) {
                                edgesToScan.push({
                                    index: vertexIndex,
                                    start: new THREE.Vector3(pos.getX(vertexIndex), pos.getY(vertexIndex), pos.getZ(vertexIndex)),
                                    end: new THREE.Vector3(pos.getX(vertexIndex + 1), pos.getY(vertexIndex + 1), pos.getZ(vertexIndex + 1))
                                });
                            }
                        }

                        if (socketIds.length > 0 || Object.keys(socketIdsMap).length > 0 ||
                            buatIds.length > 0 || Object.keys(buatIdsMap).length > 0 ||
                            wireMarkerIds.length > 0 || Object.keys(wireMarkerIdsMap).length > 0 ||
                            machineIds.length > 0 || Object.keys(machineIdsMap).length > 0 ||
                            bellIds.length > 0 || Object.keys(bellIdsMap).length > 0) {

                            // Her kenar için komponentleri bul
                            edgesToScan.forEach((edge) => {
                                const line3 = new THREE.Line3(edge.start, edge.end);
                                // const edgeType = edgeIdx === 0 ? 'left' : 'right'; // Deprecated for chain logic

                                scene.traverse(child => {
                                    const childId = child.userData.id;

                                    const isTarget = childId && (
                                        socketIdsMap[childId] ||
                                        socketIds.includes(childId) ||
                                        buatIdsMap[childId] ||
                                        buatIds.includes(childId) ||
                                        wireMarkerIdsMap[childId] ||
                                        wireMarkerIds.includes(childId) ||
                                        machineIdsMap[childId] ||
                                        machineIds.includes(childId) ||
                                        bellIdsMap[childId] ||
                                        bellIds.includes(childId)
                                    );

                                    if (isTarget) {
                                        const closestPoint = new THREE.Vector3();
                                        line3.closestPointToPoint(child.position, true, closestPoint);
                                        const dist = child.position.distanceTo(closestPoint);

                                        if (dist < 0.5) {
                                            // Aynı komponenti iki kez ekleme
                                            const alreadyAdded = originalSockets.current.some(s => s.object === child);
                                            if (!alreadyAdded) {
                                                // ✅ Wire marker ise işaretle (her zaman ortada kalmalı)
                                                const isWireMarker = child.userData?.role === 'wireMarker';

                                                // Parametrik pozisyonu hesapla (t = 0-1 arası)
                                                const edgeVector = new THREE.Vector3().subVectors(edge.end, edge.start);
                                                const edgeLength = edgeVector.length();
                                                const toPoint = new THREE.Vector3().subVectors(closestPoint, edge.start);
                                                const t = edgeLength > 0 ? toPoint.dot(edgeVector) / (edgeLength * edgeLength) : 0;

                                                originalSockets.current.push({
                                                    object: child,
                                                    originalPosition: child.position.clone(),
                                                    edgeIndex: edge.index, // Store absolute edge index
                                                    t: isWireMarker ? 0.5 : t, // ✅ Wire marker her zaman ortada (t=0.5)
                                                    isWireMarker: isWireMarker, // ✅ Wire marker flag
                                                    originalEdgeStart: edge.start.clone(),
                                                    originalEdgeEnd: edge.end.clone()
                                                });
                                            }
                                        }
                                    }
                                });
                            });
                        }
                    }
                }

                // ✅ Bağlı lighting ve fixture wire'ların orijinal pozisyonlarını kaydet (vertex için)
                originalSockets.current.forEach(socketData => {
                    const obj = socketData.object;
                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle')) {
                        // ✅ Fixture wire'lar
                        if (obj.userData?.fixtureWireMapIds && obj.userData.fixtureWireMapIds.length > 0) {
                            obj.userData.fixtureWireMapIds.forEach(fixtureWireId => {
                                scene.traverse(fixtureObj => {
                                    if (fixtureObj.isLine &&
                                        fixtureObj.userData?.id === fixtureWireId &&
                                        fixtureObj.userData?.function === 'wireLightingFixture') {

                                        const fixturePos = fixtureObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: fixtureWireId,
                                            type: 'fixture',
                                            vertex0: new THREE.Vector3(
                                                fixturePos.getX(0),
                                                fixturePos.getY(0),
                                                fixturePos.getZ(0)
                                            )
                                        });
                                    }
                                });
                            });
                        }

                        // ✅ Lighting wire'lar
                        if (obj.userData?.lightingWireMapIds && obj.userData.lightingWireMapIds.length > 0) {
                            obj.userData.lightingWireMapIds.forEach(lightingWireId => {
                                scene.traverse(lightingObj => {
                                    if (lightingObj.isLine &&
                                        lightingObj.userData?.id === lightingWireId &&
                                        (lightingObj.userData?.function === 'wireLighting' ||
                                            lightingObj.userData?.function === 'simpleLighting')) {

                                        const lightingPos = lightingObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: lightingWireId,
                                            type: 'lighting',
                                            vertex0: new THREE.Vector3(
                                                lightingPos.getX(0),
                                                lightingPos.getY(0),
                                                lightingPos.getZ(0)
                                            ),
                                            vertex1: new THREE.Vector3(
                                                lightingPos.getX(1),
                                                lightingPos.getY(1),
                                                lightingPos.getZ(1)
                                            )
                                        });
                                    }
                                });
                            });
                        }
                    }

                    // ✅ Socket wire'lar (socketBuat için - vertex gizmo)
                    if (obj.userData?.type === 'socketBuat') {
                        if (obj.userData?.socketWireMapIds && obj.userData.socketWireMapIds.length > 0) {
                            obj.userData.socketWireMapIds.forEach(socketWireId => {
                                scene.traverse(socketObj => {
                                    if (socketObj.isLine &&
                                        socketObj.userData?.id === socketWireId &&
                                        (socketObj.userData?.function === 'socket' ||
                                            socketObj.userData?.function === 'simpleSocket')) {

                                        const socketPos = socketObj.geometry.attributes.position;
                                        originalConnectedWires.current.push({
                                            wireId: socketWireId,
                                            type: 'socket',
                                            vertex0: new THREE.Vector3(
                                                socketPos.getX(0),
                                                socketPos.getY(0),
                                                socketPos.getZ(0)
                                            ),
                                            vertex1: new THREE.Vector3(
                                                socketPos.getX(1),
                                                socketPos.getY(1),
                                                socketPos.getZ(1)
                                            )
                                        });
                                    }
                                });
                            });
                        }
                    }
                });

                // ✅ Wire marker geometrilerini kaydet (ESC ile restore için)
                const currentLineId = selectedGizmo.current.userData.lineId;
                if (currentLineId) {
                    const wireMarkers = [];
                    scene.traverse(markerGroup => {
                        if (markerGroup.userData?.role === 'wireMarker' &&
                            markerGroup.userData?.parentWireId === currentLineId) {
                            // Her wire marker group'unu kaydet
                            const markerData = {
                                group: markerGroup,
                                childGeometries: []
                            };

                            // Group'un her child line geometrisini kaydet
                            markerGroup.children.forEach((child, idx) => {
                                if (child.isLine && child.geometry) {
                                    const pos = child.geometry.attributes.position;
                                    const positions = [];
                                    for (let i = 0; i < pos.count; i++) {
                                        positions.push(new THREE.Vector3().fromBufferAttribute(pos, i));
                                    }
                                    markerData.childGeometries.push({
                                        childIndex: idx,
                                        positions: positions
                                    });
                                }
                            });

                            wireMarkers.push(markerData);
                        }
                    });
                    originalWireMarkerData.current = wireMarkers;
                } else {
                    originalWireMarkerData.current = null;
                }

                isMoving.current = true;
                plane.setFromNormalAndCoplanarPoint(
                    camera.getWorldDirection(new THREE.Vector3()),
                    selectedGizmo.current.position
                );
                raycaster.ray.intersectPlane(plane, planeIntersect);
                offset.copy(planeIntersect).sub(selectedGizmo.current.position);
            } else if (isMoving.current) {
                // ✅ Sürükleme başarıyla tamamlandı

                // ===== BLOCK DRAGGING: MoveCommand ile undo-redo =====
                if (isBlockDragging.current) {
                    const blockObj = blockObjRef.current;
                    const blockId = blockObj?.userData?.id;

                    if (blockObj && blockId) {
                        let afterPos;
                        if (blockIsOCS.current) {
                            const m = blockObj.matrix.elements;
                            afterPos = { x: m[12], y: m[13], z: m[14] };
                        } else {
                            afterPos = { x: blockObj.position.x, y: blockObj.position.y, z: blockObj.position.z };
                        }

                        const positionMap = {
                            [blockId]: {
                                before: { x: originalBlockPos.current.x, y: originalBlockPos.current.y, z: originalBlockPos.current.z },
                                after: afterPos
                            }
                        };

                        const manager = window.undoRedoManager;
                        if (manager) {
                            const tx = manager.beginTransaction('Move Block');
                            manager.addCommand(new MoveCommand(positionMap));
                            manager.commitTransaction(tx);
                        }
                    }

                    // Cleanup
                    isBlockDragging.current = false;
                    blockObjRef.current = null;
                    blockIsOCS.current = false;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END BLOCK DRAGGING =====

                // ===== MTEXT MOVING: MoveCommand ile undo-redo =====
                if (isMtextMoving.current && mtextObjRef.current) {
                    const mtextObj = mtextObjRef.current;
                    const mtextId = mtextObj.userData?.id;

                    if (mtextId) {
                        const positionMap = {
                            [mtextId]: {
                                before: { x: originalMtextPos.current.x, y: originalMtextPos.current.y, z: originalMtextPos.current.z },
                                after: { x: mtextObj.position.x, y: mtextObj.position.y, z: mtextObj.position.z }
                            }
                        };

                        const manager = window.undoRedoManager;
                        if (manager) {
                            const tx = manager.beginTransaction('Move MText');
                            manager.addCommand(new MoveCommand(positionMap));
                            manager.commitTransaction(tx);
                        }
                    }

                    isMtextMoving.current = false;
                    mtextObjRef.current = null;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END MTEXT MOVING =====

                // ===== MTEXT RESIZING: GizmoEditCommand ile undo-redo =====
                if (isMtextResizing.current && mtextObjRef.current) {
                    const mtextObj = mtextObjRef.current;
                    const mtextId = mtextObj.userData?.id;

                    if (mtextId) {
                        const manager = window.undoRedoManager;
                        if (manager) {
                            const tx = manager.beginTransaction('Resize MText');
                            manager.addCommand(new GizmoEditCommand(mtextId, {
                                objectType: 'mtext',
                                before: {
                                    position: { x: originalMtextPos.current.x, y: originalMtextPos.current.y, z: originalMtextPos.current.z },
                                    scale: { x: originalMtextScale.current.x, y: originalMtextScale.current.y, z: originalMtextScale.current.z },
                                    userData: { width: originalMtextScale.current.x, height: originalMtextScale.current.y }
                                },
                                after: {
                                    position: { x: mtextObj.position.x, y: mtextObj.position.y, z: mtextObj.position.z },
                                    scale: { x: mtextObj.scale.x, y: mtextObj.scale.y, z: mtextObj.scale.z },
                                    userData: { width: mtextObj.scale.x, height: mtextObj.scale.y }
                                }
                            }));
                            manager.commitTransaction(tx);
                        }
                    }

                    isMtextResizing.current = false;
                    mtextObjRef.current = null;
                    mtextCornerIdx.current = -1;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END MTEXT RESIZING =====

                // ===== SIMPLE DRAWING: userData güncelle =====
                if (isSimpleDrawing.current) {
                    const lineId = selectedGizmo.current?.userData?.lineId;
                    // Before state'i kaydet (originalAllVertices zaten ref'te)
                    const beforeVertices = originalAllVertices.current.map(v => ({ x: v.x, y: v.y, z: v.z }));
                    let beforeUserData = {};
                    let afterVertices = [];
                    let afterUserData = {};

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === lineId) {
                            const pos = obj.geometry.attributes.position;

                            // After vertices'i oku
                            for (let i = 0; i < pos.count; i++) {
                                afterVertices.push({ x: pos.getX(i), y: pos.getY(i), z: pos.getZ(i) });
                            }

                            // Before userData'yı kaydet
                            if (obj.userData.start !== undefined) beforeUserData.start = { ...originalAllVertices.current[0] };
                            if (obj.userData.end !== undefined) {
                                const lastIdx = originalAllVertices.current.length - 1;
                                beforeUserData.end = { ...originalAllVertices.current[lastIdx] };
                            }

                            // start/end varsa güncelle (line tipi nesneler)
                            if (pos.count >= 2) {
                                if (obj.userData.start !== undefined) {
                                    obj.userData.start = { x: pos.getX(0), y: pos.getY(0), z: pos.getZ(0) };
                                }
                                if (obj.userData.end !== undefined) {
                                    const last = pos.count - 1;
                                    obj.userData.end = { x: pos.getX(last), y: pos.getY(last), z: pos.getZ(last) };
                                }
                            }
                            // vertices dizisi varsa güncelle (polyline/spline tipi nesneler)
                            if (obj.userData.vertices && Array.isArray(obj.userData.vertices)) {
                                for (let i = 0; i < pos.count && i < obj.userData.vertices.length; i++) {
                                    obj.userData.vertices[i] = {
                                        x: pos.getX(i), y: pos.getY(i), z: pos.getZ(i)
                                    };
                                }
                            }

                            // After userData
                            if (obj.userData.start !== undefined) afterUserData.start = { ...obj.userData.start };
                            if (obj.userData.end !== undefined) afterUserData.end = { ...obj.userData.end };
                        }
                    });

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager && afterVertices.length > 0) {
                        const tx = manager.beginTransaction('Edit Line');
                        manager.addCommand(new GizmoEditCommand(lineId, {
                            objectType: 'line',
                            before: { vertices: beforeVertices, userData: beforeUserData },
                            after: { vertices: afterVertices, userData: afterUserData }
                        }));
                        manager.commitTransaction(tx);
                    }

                    isSimpleDrawing.current = false;
                    originalAllVertices.current = [];
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END SIMPLE DRAWING =====

                // ===== CIRCLE DRAWING: userData güncelle =====
                if (isCircleDrawing.current) {
                    const circleId = selectedGizmo.current?.userData?.circleId;
                    const origCircle = originalCircleData.current;
                    const beforeVertices = origCircle.vertices.map(v => ({ x: v.x, y: v.y, z: v.z }));
                    let afterVertices = [];
                    let afterCircleUserData = null;

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === circleId) {
                            const pos = obj.geometry.attributes.position;

                            for (let i = 0; i < pos.count; i++) {
                                afterVertices.push({ x: pos.getX(i), y: pos.getY(i), z: pos.getZ(i) });
                            }

                            const p0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                            const pHalf = new THREE.Vector3(
                                pos.getX(Math.floor(pos.count / 2)),
                                pos.getY(Math.floor(pos.count / 2)),
                                pos.getZ(Math.floor(pos.count / 2))
                            );
                            const newCenter = p0.clone().add(pHalf).multiplyScalar(0.5);
                            const newRadius = p0.distanceTo(newCenter);
                            obj.userData.center = { x: newCenter.x, y: newCenter.y, z: newCenter.z };
                            obj.userData.radius = newRadius;

                            afterCircleUserData = {
                                center: { x: newCenter.x, y: newCenter.y, z: newCenter.z },
                                radius: newRadius
                            };
                        }
                    });

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager && afterVertices.length > 0 && afterCircleUserData) {
                        const tx = manager.beginTransaction('Edit Circle');
                        manager.addCommand(new GizmoEditCommand(circleId, {
                            objectType: 'circle',
                            before: {
                                vertices: beforeVertices,
                                userData: { center: { ...origCircle.center }, radius: origCircle.radius }
                            },
                            after: {
                                vertices: afterVertices,
                                userData: afterCircleUserData
                            }
                        }));
                        manager.commitTransaction(tx);
                    }

                    isCircleDrawing.current = false;
                    originalCircleData.current = null;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END CIRCLE DRAWING =====

                // ===== ARC DRAWING: userData güncelle =====
                if (isArcDrawing.current) {
                    const arcId = selectedGizmo.current?.userData?.arcId;
                    const origArc = originalArcData.current;
                    const beforeVertices = origArc.vertices.map(v => ({ x: v.x, y: v.y, z: v.z }));
                    let afterVertices = [];
                    let afterArcUserData = null;

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === arcId) {
                            const pos = obj.geometry.attributes.position;

                            for (let i = 0; i < pos.count; i++) {
                                afterVertices.push({ x: pos.getX(i), y: pos.getY(i), z: pos.getZ(i) });
                            }

                            const p0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                            const pLast = new THREE.Vector3(
                                pos.getX(pos.count - 1), pos.getY(pos.count - 1), pos.getZ(pos.count - 1)
                            );
                            const pMid = new THREE.Vector3(
                                pos.getX(Math.floor(pos.count / 2)),
                                pos.getY(Math.floor(pos.count / 2)),
                                pos.getZ(Math.floor(pos.count / 2))
                            );
                            const center = circumcenter(p0, pMid, pLast);
                            if (center) {
                                const newRadius = p0.distanceTo(center);
                                const newStartAngle = Math.atan2(p0.y - center.y, p0.x - center.x);
                                let newEndAngle = Math.atan2(pLast.y - center.y, pLast.x - center.x);
                                if (newEndAngle <= newStartAngle) newEndAngle += Math.PI * 2;
                                obj.userData.center = { x: center.x, y: center.y, z: 0 };
                                obj.userData.radius = newRadius;
                                obj.userData.startAngle = newStartAngle;
                                obj.userData.endAngle = newEndAngle;
                                afterArcUserData = {
                                    center: { x: center.x, y: center.y, z: 0 },
                                    radius: newRadius,
                                    startAngle: newStartAngle,
                                    endAngle: newEndAngle
                                };
                            }
                        }
                    });

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager && afterVertices.length > 0 && afterArcUserData) {
                        const tx = manager.beginTransaction('Edit Arc');
                        manager.addCommand(new GizmoEditCommand(arcId, {
                            objectType: 'arc',
                            before: {
                                vertices: beforeVertices,
                                userData: {
                                    center: { ...origArc.center },
                                    radius: origArc.radius,
                                    startAngle: origArc.startAngle,
                                    endAngle: origArc.endAngle
                                }
                            },
                            after: {
                                vertices: afterVertices,
                                userData: afterArcUserData
                            }
                        }));
                        manager.commitTransaction(tx);
                    }

                    isArcDrawing.current = false;
                    originalArcData.current = null;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END ARC DRAWING =====

                // ===== ELLIPSE DRAWING: userData güncelle =====
                if (isEllipseDrawing.current) {
                    const ellipseId = selectedGizmo.current?.userData?.ellipseId;
                    const origEllipse = originalEllipseData.current;
                    const beforeVertices = origEllipse.vertices.map(v => ({ x: v.x, y: v.y, z: v.z }));
                    let afterVertices = [];
                    let afterEllipseUserData = null;

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === ellipseId) {
                            const pos = obj.geometry.attributes.position;

                            for (let i = 0; i < pos.count; i++) {
                                afterVertices.push({ x: pos.getX(i), y: pos.getY(i), z: pos.getZ(i) });
                            }

                            const segments = pos.count - 1;
                            const p0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                            const pHalf = new THREE.Vector3(
                                pos.getX(Math.floor(segments / 2)),
                                pos.getY(Math.floor(segments / 2)),
                                pos.getZ(Math.floor(segments / 2))
                            );
                            const newCenter = p0.clone().add(pHalf).multiplyScalar(0.5);
                            const pQ1 = new THREE.Vector3(
                                pos.getX(Math.floor(segments / 4)),
                                pos.getY(Math.floor(segments / 4)),
                                pos.getZ(Math.floor(segments / 4))
                            );
                            const newRadiusX = p0.distanceTo(newCenter);
                            const newRadiusY = pQ1.distanceTo(newCenter);
                            obj.userData.center = { x: newCenter.x, y: newCenter.y, z: newCenter.z };
                            obj.userData.radiusX = newRadiusX;
                            obj.userData.radiusY = newRadiusY;

                            afterEllipseUserData = {
                                center: { x: newCenter.x, y: newCenter.y, z: newCenter.z },
                                radiusX: newRadiusX,
                                radiusY: newRadiusY,
                                rotation: obj.userData.rotation || 0
                            };
                        }
                    });

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager && afterVertices.length > 0 && afterEllipseUserData) {
                        const tx = manager.beginTransaction('Edit Ellipse');
                        manager.addCommand(new GizmoEditCommand(ellipseId, {
                            objectType: 'ellipse',
                            before: {
                                vertices: beforeVertices,
                                userData: {
                                    center: { ...origEllipse.center },
                                    radiusX: origEllipse.radiusX,
                                    radiusY: origEllipse.radiusY,
                                    rotation: origEllipse.rotation
                                }
                            },
                            after: {
                                vertices: afterVertices,
                                userData: afterEllipseUserData
                            }
                        }));
                        manager.commitTransaction(tx);
                    }

                    isEllipseDrawing.current = false;
                    originalEllipseData.current = null;
                    selectedGizmo.current = null;
                    isMoving.current = false;
                    return;
                }
                // ===== END ELLIPSE DRAWING =====

                // ── Wire geometry undo/redo: drag bitti — değişiklikleri komut olarak kaydet ──
                {
                    const _undoMgr = window.undoRedoManager;
                    if (_undoMgr && wireBeforeId.current && currentWireObj.current) {
                        const _wObj = currentWireObj.current;
                        const _wPos = _wObj.geometry?.attributes?.position;
                        if (_wPos) {
                            const _afterVerts = [];
                            for (let _i = 0; _i < _wPos.count; _i++) {
                                _afterVerts.push({ x: _wPos.getX(_i), y: _wPos.getY(_i), z: _wPos.getZ(_i) });
                            }
                            const _beforeVerts = wireBeforeVertices.current;
                            const _changed = _beforeVerts.length > 0 && _afterVerts.some((_v, _i) => {
                                const _b = _beforeVerts[_i];
                                return !_b || Math.abs(_v.x - _b.x) > 0.01 || Math.abs(_v.y - _b.y) > 0.01;
                            });
                            if (_changed) {
                                const _wireChanges = [{
                                    id: wireBeforeId.current,
                                    before: _beforeVerts,
                                    after: _afterVerts
                                }];

                                // Panel çıkış swap: diğer değişen wire'ları ekle
                                for (const _pwBefore of allPanelWiresBeforeState.current) {
                                    if (_pwBefore.wireId === wireBeforeId.current) continue;
                                    let _otherObj = null;
                                    scene.traverse(_o => { if (_o.isLine && _o.userData.id === _pwBefore.wireId) _otherObj = _o; });
                                    if (!_otherObj) continue;
                                    const _oPos = _otherObj.geometry?.attributes?.position;
                                    if (!_oPos) continue;
                                    const _oAfter = [];
                                    for (let _i = 0; _i < _oPos.count; _i++) {
                                        _oAfter.push({ x: _oPos.getX(_i), y: _oPos.getY(_i), z: _oPos.getZ(_i) });
                                    }
                                    const _oChanged = _oAfter.some((_v, _i) => {
                                        const _b = _pwBefore.vertices[_i];
                                        return !_b || Math.abs(_v.x - _b.x) > 0.01 || Math.abs(_v.y - _b.y) > 0.01;
                                    });
                                    if (_oChanged) {
                                        _wireChanges.push({ id: _pwBefore.wireId, before: _pwBefore.vertices, after: _oAfter });
                                    }
                                }

                                // Bağlı lighting/fixture wire değişimleri
                                for (const _wd of (originalConnectedWires.current || [])) {
                                    if (!_wd.wireId) continue;
                                    let _cwObj = null;
                                    scene.traverse(_o => { if (_o.isLine && _o.userData.id === _wd.wireId) _cwObj = _o; });
                                    if (!_cwObj) continue;
                                    const _cwPos = _cwObj.geometry?.attributes?.position;
                                    if (!_cwPos) continue;
                                    const _cwAfter = [];
                                    for (let _i = 0; _i < _cwPos.count; _i++) {
                                        _cwAfter.push({ x: _cwPos.getX(_i), y: _cwPos.getY(_i), z: _cwPos.getZ(_i) });
                                    }
                                    // Before-state: originalConnectedWires'daki vertex0/vertex1'den yeniden oluştur
                                    const _cwBefore = _cwAfter.map(_v => ({ ..._v }));
                                    if (_wd.vertex0) { _cwBefore[0] = { x: _wd.vertex0.x, y: _wd.vertex0.y, z: _wd.vertex0.z }; }
                                    if (_wd.vertex1) { _cwBefore[1] = { x: _wd.vertex1.x, y: _wd.vertex1.y, z: _wd.vertex1.z }; }
                                    const _cwChanged = _cwAfter.some((_v, _i) => {
                                        const _b = _cwBefore[_i];
                                        return !_b || Math.abs(_v.x - _b.x) > 0.01 || Math.abs(_v.y - _b.y) > 0.01;
                                    });
                                    if (_cwChanged) {
                                        _wireChanges.push({ id: _wd.wireId, before: _cwBefore, after: _cwAfter });
                                    }
                                }

                                // Komponent pozisyon değişimleri
                                const _compChanges = (originalSockets.current || [])
                                    .filter(_s => _s.object?.userData?.id)
                                    .map(_s => ({
                                        id: _s.object.userData.id,
                                        before: { x: _s.originalPosition.x, y: _s.originalPosition.y, z: _s.originalPosition.z },
                                        after: { x: _s.object.position.x, y: _s.object.position.y, z: _s.object.position.z }
                                    }));

                                const _tx = _undoMgr.beginTransaction('Edit Wire');
                                _undoMgr.addCommand(new WireGeometryEditCommand(_wireChanges, _compChanges));
                                _undoMgr.commitTransaction(_tx);
                            }
                        }
                    }
                    // Capture ref'lerini temizle
                    wireBeforeVertices.current = [];
                    wireBeforeId.current = null;
                    allPanelWiresBeforeState.current = [];
                }

                // 🔧 Panel çıkış sıralaması değiştiyse, etiket numaralarını güncelle
                if (currentPanelId.current) {
                    const panelID = currentPanelId.current;

                    // Etiket numaralarını güncelle (wire exitIndex'leri de bu fonksiyonda güncelleniyor)
                    // ✅ YENİ: feeder titleNumber güncellemelerini al ve Redux'a gönder
                    const feederTitleUpdates = updateWireTitleNumbers(scene, panelID, feeders);

                    // Redux'taki feeder'ların titleNumber değerlerini güncelle
                    if (feederTitleUpdates && feederTitleUpdates.length > 0) {
                        feederTitleUpdates.forEach(({ feederId, titleNumber }) => {
                            dispatch(updateFeeder({
                                id: feederId,
                                changes: {
                                    titleNumber
                                }
                            }));
                        });
                    }
                }

                selectedGizmo.current = null;
                isMoving.current = false;
                originalWireMarkerData.current = null; // ✅ Wire marker data'yı temizle
            }
        };

        // Drag boyunca obje id→ref cache. İlk lookup'ta lazy build, sonra O(1).
        // Mouseup'ta temizlenir. Bu sayede mousemove'daki find-by-id traverse'leri
        // tek bir traverse'e indirgenir.
        const _findById = (id) => {
            if (id == null) return null;
            if (!dragIdMapRef.current) {
                const m = new Map();
                scene.traverse(o => {
                    const oid = o.userData?.id;
                    if (oid != null && !m.has(oid)) m.set(oid, o);
                });
                dragIdMapRef.current = m;
            }
            const obj = dragIdMapRef.current.get(id);
            if (obj && !obj.parent) {
                // Sahneden silindiyse cache'den de kaldır
                dragIdMapRef.current.delete(id);
                return null;
            }
            return obj || null;
        };

        const onMouseMove = (event) => {
            if (!isMoving.current || !selectedGizmo.current) return;
            const mouse = getMouse(event);
            raycaster.setFromCamera(mouse, camera);
            raycaster.ray.intersectPlane(plane, planeIntersect);

            const gizmoTargetPosition = planeIntersect.clone().sub(offset);
            let snapped = false;
            let finalTarget = gizmoTargetPosition.clone();

            // ✅ Orto mode kontrolü - hedef pozisyonu sınırlandır
            // NOT: Vertex gizmo orto modundan etkilenmez
            const gizmoTypeForOrtho = selectedGizmo.current.userData.type;
            if (orthoMode && gizmoTypeForOrtho !== 'vertex') {
                const deltaX = Math.abs(finalTarget.x - originalPosition.current.x);
                const deltaY = Math.abs(finalTarget.y - originalPosition.current.y);

                // Hangi eksen daha fazla hareket etmişse, o eksende ilerle
                if (deltaX > deltaY) {
                    // X ekseninde hareket et, Y'yi sabitle
                    finalTarget.y = originalPosition.current.y;
                } else {
                    // Y ekseninde hareket et, X'i sabitle
                    finalTarget.x = originalPosition.current.x;
                }
            }

            // ✅ Grid mode kontrolü - 5'er birimlik adımlar
            // NOT: Vertex gizmo için grid snap offset hesaplamasında yapılıyor
            const gizmoType = selectedGizmo.current.userData.type;
            if (gridMode && gizmoType !== 'vertex') {
                const gridSize = 5;
                // ✅ Başlangıç noktasına GÖRECELİ 5'er birim adım (mutlak grid değil).
                // Mutlak snap'te ilk adım <5 birim oluyordu (orijinal konum grid'e hizalı
                // değilse) → 5 birim ötedeki kenara tam oturmuyordu. Göreceli snap ile
                // her adım tam 5 birim → üst üste gelen kenar tam üzerine sürüklenip
                // yer değiştirebiliyor.
                const op = originalPosition.current;
                finalTarget.x = op.x + Math.round((finalTarget.x - op.x) / gridSize) * gridSize;
                finalTarget.y = op.y + Math.round((finalTarget.y - op.y) / gridSize) * gridSize;
                finalTarget.z = op.z + Math.round((finalTarget.z - op.z) / gridSize) * gridSize;
            }

            // Snap points: 500ms TTL cache — drag boyunca her mousemove'da full
            // scene.traverse + O(N²) segment kesişim hesabı tekrarlanmasın.
            const _now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            if (!snapCacheRef.current.points || (_now - snapCacheRef.current.time) > 500) {
                snapCacheRef.current.points = getSnapPointsFromScene(scene, floorPlans, wires);
                snapCacheRef.current.time = _now;
            }
            const snapPoints = snapCacheRef.current.points;
            const snapDist = getSnapDistance();
            const previewThreshold = snapDist * 2;

            let closestSnap = null;
            let minDistance = Infinity;

            for (let sp of snapPoints) {
                // 🟡 Yalnızca sabit nesnelere snap (gizmo'nun kendisini dışla)
                /*    if (sp.object && sp.object.uuid === selectedGizmo.current.uuid) continue; */
                if (selectedGizmo.current.userData && selectedGizmo.current.userData.objUUID != sp.no) {
                    const distance = gizmoTargetPosition.distanceTo(sp.position);
                    if (distance < 20) {
                        minDistance = distance;
                        closestSnap = sp;

                    }
                }
            }
            // 🟡 Önizleme: sadece yakınsa göster — her seferinde yeniden oluştur (tip değişebilir)
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                if (snapMarkerRef.current.geometry) snapMarkerRef.current.geometry.dispose();
                if (snapMarkerRef.current.material) snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }
            if (closestSnap && minDistance < previewThreshold) {
                const marker = createSnapMarker(closestSnap);
                marker.position.copy(closestSnap.position);
                marker.scale.setScalar(1);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // ✅ Snap uygula (yakınlık yeterliyse)
            if (closestSnap && minDistance < snapDist) {
                finalTarget.copy(closestSnap.position);
                snapped = true;
                snappedPointRef.current = finalTarget.clone();
            } else {
                snappedPointRef.current = null;
            }

            // 🎯 Hareket işlemi

            // ===== BLOCK DRAGGING HANDLING =====
            if (isBlockDragging.current && blockObjRef.current) {
                const delta = finalTarget.clone().sub(originalPosition.current);
                const newPos = originalBlockPos.current.clone().add(delta);

                if (blockIsOCS.current) {
                    // OCS block: matrix translation'ı güncelle
                    const m = blockObjRef.current.matrix.elements;
                    m[12] = newPos.x;
                    m[13] = newPos.y;
                    m[14] = newPos.z;
                    blockObjRef.current.matrixWorldNeedsUpdate = true;
                } else {
                    blockObjRef.current.position.copy(newPos);
                }

                selectedGizmo.current.position.copy(finalTarget);
                return;
            }
            // ===== END BLOCK DRAGGING =====

            // ===== MTEXT MOVING HANDLING =====
            if (isMtextMoving.current && mtextObjRef.current) {
                const delta = finalTarget.clone().sub(originalPosition.current);
                const newPos = originalMtextPos.current.clone().add(delta);
                mtextObjRef.current.position.copy(newPos);
                selectedGizmo.current.position.copy(finalTarget);

                // Köşe gizmoları ve border'ı da taşı
                updateMtextGizmos(scene, mtextObjRef.current);
                return;
            }
            // ===== END MTEXT MOVING =====

            // ===== MTEXT RESIZING HANDLING =====
            if (isMtextResizing.current && mtextObjRef.current) {
                const fixed = mtextFixedCorner.current;
                const drag = finalTarget;

                const newWidth = Math.max(10, Math.abs(drag.x - fixed.x));
                const newHeight = Math.max(10, Math.abs(drag.y - fixed.y));
                const newPosX = Math.min(drag.x, fixed.x);
                const newPosY = Math.max(drag.y, fixed.y);

                if (mtextObjRef.current.isSprite) {
                    mtextObjRef.current.position.set(newPosX, newPosY, 0);
                    rerenderMtextSprite(mtextObjRef.current, newWidth, newHeight);
                } else {
                    mtextObjRef.current.position.set(newPosX, newPosY, 0);
                    if (mtextObjRef.current.maxWidth !== undefined) {
                        mtextObjRef.current.maxWidth = newWidth;
                        if (mtextObjRef.current.sync) mtextObjRef.current.sync();
                    }
                }

                selectedGizmo.current.position.copy(finalTarget);

                // Diğer köşe gizmoları, merkez ve border'ı güncelle
                updateMtextGizmos(scene, mtextObjRef.current);
                return;
            }
            // ===== END MTEXT RESIZING =====

            const { type, lineId, vertexIndex, edgeIndex } = selectedGizmo.current.userData;

            // ===== SIMPLE DRAWING OBJECT HANDLING =====
            if (isSimpleDrawing.current) {
                const _ho = _findById(lineId);
                const targetObj = (_ho && _ho.isLine) ? _ho : null;

                if (targetObj) {
                    const pos = targetObj.geometry.attributes.position;

                    if (type === 'midpoint') {
                        // TRANSLATE: Tüm vertexleri aynı delta kadar taşı
                        const delta = finalTarget.clone().sub(originalPosition.current);
                        for (let i = 0; i < originalAllVertices.current.length; i++) {
                            const newP = originalAllVertices.current[i].clone().add(delta);
                            pos.setXYZ(i, newP.x, newP.y, newP.z);
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    } else if (type === 'vertex') {
                        // STRETCH: Sadece bu vertex'i taşı
                        pos.setXYZ(vertexIndex, finalTarget.x, finalTarget.y, finalTarget.z);
                        selectedGizmo.current.position.copy(finalTarget);
                    }

                    pos.needsUpdate = true;
                    targetObj.geometry.computeBoundingSphere();
                }

                updateGizmos(scene, selectedGizmo.current);
                return; // Wire-specific mantığı atla
            }
            // ===== END SIMPLE DRAWING =====

            // ===== CIRCLE DRAWING HANDLING =====
            if (isCircleDrawing.current) {
                const { type: circleGizmoType, circleId } = selectedGizmo.current.userData;
                const _ho = _findById(circleId);
                const circleObj = (_ho && _ho.isLine) ? _ho : null;

                if (circleObj) {
                    const pos = circleObj.geometry.attributes.position;
                    const origData = originalCircleData.current;
                    const origCenter = new THREE.Vector3(origData.center.x, origData.center.y, origData.center.z || 0);

                    if (circleGizmoType === 'circleCenter') {
                        // TRANSLATE: Tüm vertexleri aynı delta kadar taşı
                        const delta = finalTarget.clone().sub(originalPosition.current);
                        for (let i = 0; i < origData.vertices.length; i++) {
                            const newP = origData.vertices[i].clone().add(delta);
                            pos.setXYZ(i, newP.x, newP.y, newP.z);
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    } else if (circleGizmoType === 'circleQuarter') {
                        // RADIUS CHANGE: Yeni yarıçapı hesapla ve tüm noktaları yeniden oluştur
                        const newRadius = finalTarget.distanceTo(origCenter);
                        const segments = pos.count - 1; // 65 nokta = 64 segment + kapanış noktası
                        const angleStep = (Math.PI * 2) / segments;
                        for (let i = 0; i <= segments; i++) {
                            const angle = i * angleStep;
                            pos.setXYZ(i,
                                origCenter.x + newRadius * Math.cos(angle),
                                origCenter.y + newRadius * Math.sin(angle),
                                origCenter.z
                            );
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    }

                    pos.needsUpdate = true;
                    circleObj.geometry.computeBoundingSphere();
                }

                // Circle gizmo'larını güncelle (diğer gizmo'ların pozisyonları)
                updateCircleGizmos(scene, selectedGizmo.current, originalCircleData.current);
                return;
            }
            // ===== END CIRCLE DRAWING =====

            // ===== ARC DRAWING HANDLING =====
            if (isArcDrawing.current) {
                const { type: arcGizmoType, arcId } = selectedGizmo.current.userData;
                const _ho = _findById(arcId);
                const arcObj = (_ho && _ho.isLine) ? _ho : null;

                if (arcObj) {
                    const pos = arcObj.geometry.attributes.position;
                    const origData = originalArcData.current;
                    const origCenter = new THREE.Vector3(origData.center.x, origData.center.y, origData.center.z || 0);
                    const R = origData.radius;

                    if (arcGizmoType === 'arcCenter') {
                        // TRANSLATE: Tüm vertexleri aynı delta kadar taşı
                        const delta = finalTarget.clone().sub(originalPosition.current);
                        for (let i = 0; i < origData.vertices.length; i++) {
                            const newP = origData.vertices[i].clone().add(delta);
                            pos.setXYZ(i, newP.x, newP.y, newP.z);
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    } else if (arcGizmoType === 'arcStart') {
                        // START ANGLE CHANGE: Gizmo arc yayı üzerinde kalır
                        const newStartAngle = Math.atan2(
                            finalTarget.y - origCenter.y,
                            finalTarget.x - origCenter.x
                        );
                        let endAngle = origData.endAngle;
                        if (endAngle <= newStartAngle) endAngle += Math.PI * 2;
                        const angleStep = (endAngle - newStartAngle) / (pos.count - 1);
                        for (let i = 0; i < pos.count; i++) {
                            const angle = newStartAngle + i * angleStep;
                            pos.setXYZ(i,
                                origCenter.x + R * Math.cos(angle),
                                origCenter.y + R * Math.sin(angle),
                                origCenter.z
                            );
                        }
                        // Gizmo'yu arc üzerindeki noktaya kısıtla (mouse'u takip etmesin)
                        selectedGizmo.current.position.set(
                            origCenter.x + R * Math.cos(newStartAngle),
                            origCenter.y + R * Math.sin(newStartAngle),
                            origCenter.z
                        );
                    } else if (arcGizmoType === 'arcEnd') {
                        // END ANGLE CHANGE: Gizmo arc yayı üzerinde kalır
                        let newEndAngle = Math.atan2(
                            finalTarget.y - origCenter.y,
                            finalTarget.x - origCenter.x
                        );
                        const startAngle = origData.startAngle;
                        if (newEndAngle <= startAngle) newEndAngle += Math.PI * 2;
                        const angleStep = (newEndAngle - startAngle) / (pos.count - 1);
                        for (let i = 0; i < pos.count; i++) {
                            const angle = startAngle + i * angleStep;
                            pos.setXYZ(i,
                                origCenter.x + R * Math.cos(angle),
                                origCenter.y + R * Math.sin(angle),
                                origCenter.z
                            );
                        }
                        // Gizmo'yu arc üzerindeki noktaya kısıtla
                        selectedGizmo.current.position.set(
                            origCenter.x + R * Math.cos(newEndAngle),
                            origCenter.y + R * Math.sin(newEndAngle),
                            origCenter.z
                        );
                    } else if (arcGizmoType === 'arcMidpoint') {
                        // RADIUS CHANGE: Yeni yarıçap = center'dan sürüklenen noktaya mesafe
                        const newRadius = finalTarget.distanceTo(origCenter);
                        const startAngle = origData.startAngle;
                        const endAngle = origData.endAngle;
                        const angleStep = (endAngle - startAngle) / (pos.count - 1);
                        for (let i = 0; i < pos.count; i++) {
                            const angle = startAngle + i * angleStep;
                            pos.setXYZ(i,
                                origCenter.x + newRadius * Math.cos(angle),
                                origCenter.y + newRadius * Math.sin(angle),
                                origCenter.z
                            );
                        }
                        // Gizmo'yu arc midpoint üzerinde tut
                        const midAngle = (startAngle + endAngle) / 2;
                        selectedGizmo.current.position.set(
                            origCenter.x + newRadius * Math.cos(midAngle),
                            origCenter.y + newRadius * Math.sin(midAngle),
                            origCenter.z
                        );
                    }

                    pos.needsUpdate = true;
                    arcObj.geometry.computeBoundingSphere();
                }

                updateArcGizmos(scene, selectedGizmo.current, originalArcData.current);
                return;
            }
            // ===== END ARC DRAWING =====

            // ===== ELLIPSE DRAWING HANDLING =====
            if (isEllipseDrawing.current) {
                const { type: ellipseGizmoType, ellipseId, quarterIndex } = selectedGizmo.current.userData;
                const _ho = _findById(ellipseId);
                const ellipseObj = (_ho && _ho.isLine) ? _ho : null;

                if (ellipseObj) {
                    const pos = ellipseObj.geometry.attributes.position;
                    const origData = originalEllipseData.current;
                    const origCenter = new THREE.Vector3(origData.center.x, origData.center.y, origData.center.z || 0);
                    const rotation = origData.rotation;

                    if (ellipseGizmoType === 'ellipseCenter') {
                        // TRANSLATE: Tüm vertexleri aynı delta kadar taşı
                        const delta = finalTarget.clone().sub(originalPosition.current);
                        for (let i = 0; i < origData.vertices.length; i++) {
                            const newP = origData.vertices[i].clone().add(delta);
                            pos.setXYZ(i, newP.x, newP.y, newP.z);
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    } else if (ellipseGizmoType === 'ellipseQuarter') {
                        // RADIUS CHANGE: Quarter index'e göre radiusX veya radiusY değiştir
                        const dx = finalTarget.x - origCenter.x;
                        const dy = finalTarget.y - origCenter.y;
                        // Local koordinatlara dönüştür (rotation'ı geri al)
                        const cosR = Math.cos(-rotation);
                        const sinR = Math.sin(-rotation);
                        const localX = dx * cosR - dy * sinR;
                        const localY = dx * sinR + dy * cosR;

                        let newRadiusX = origData.radiusX;
                        let newRadiusY = origData.radiusY;
                        if (quarterIndex === 0 || quarterIndex === 2) {
                            newRadiusX = Math.abs(localX);
                        } else {
                            newRadiusY = Math.abs(localY);
                        }

                        // Tüm noktaları yeniden oluştur
                        const segments = pos.count - 1;
                        const angleStep = (Math.PI * 2) / segments;
                        const cosRot = Math.cos(rotation);
                        const sinRot = Math.sin(rotation);
                        for (let i = 0; i <= segments; i++) {
                            const theta = i * angleStep;
                            const ex = newRadiusX * Math.cos(theta);
                            const ey = newRadiusY * Math.sin(theta);
                            pos.setXYZ(i,
                                origCenter.x + ex * cosRot - ey * sinRot,
                                origCenter.y + ex * sinRot + ey * cosRot,
                                origCenter.z
                            );
                        }
                        selectedGizmo.current.position.copy(finalTarget);
                    }

                    pos.needsUpdate = true;
                    ellipseObj.geometry.computeBoundingSphere();
                }

                updateEllipseGizmos(scene, selectedGizmo.current, originalEllipseData.current);
                return;
            }
            // ===== END ELLIPSE DRAWING =====

            // ✅ Panel çıkış kısıtlaması
            if (connectedPanelExitPoints.current && connectedPanelExitPoints.current.length > 0) {
                // Case 1: Vertex 0 (Start)
                if ((type === 'vertex' && vertexIndex === 1) || type === 'start') {
                    let closestExit = null;
                    let closestExitIndex = -1;
                    let minExitDist = Infinity;

                    connectedPanelExitPoints.current.forEach((ep, idx) => {
                        const d = gizmoTargetPosition.distanceTo(ep);
                        if (d < minExitDist) {
                            minExitDist = d;
                            closestExit = ep;
                            closestExitIndex = idx;
                        }
                    });

                    if (closestExit) {
                        // Phase 2: Bundle aralığına swap engelle. closestExitIndex bir bundle'ın
                        // panelExitRange'inde ve dragged wire o bundle üyesi DEĞİLSE, swap'i atla
                        // (snap noktasını da değiştirme — wire kendi yerinde kalır).
                        const _draggedLineId = selectedGizmo.current?.userData?.lineId;
                        const _panelId = currentPanelId.current;
                        if (_panelId && _draggedLineId) {
                            const _bundle = findBundleByPanelExitIndex(scene, _panelId, closestExitIndex);
                            if (_bundle) {
                                const _wireIds = _bundle.userData?.wireIds || [];
                                const _isMember = _wireIds.some(id => String(id) === String(_draggedLineId));
                                if (!_isMember) {
                                    // Bundle içine girilmek isteniyor — engelle
                                    closestExit = null;
                                    closestExitIndex = -1;
                                }
                            }
                        }
                    }

                    if (closestExit) {
                        // ✅ Panel açılı olduğunda da çalışması için panel ekseni boyunca hareket et
                        // Y-only değil, initialExitPoint'ten closestExit'e olan offset'i uygula
                        if (initialExitPoint.current) {
                            const offset = new THREE.Vector3().subVectors(closestExit, initialExitPoint.current);
                            finalTarget.copy(originalPosition.current.clone().add(offset));
                        } else {
                            finalTarget.copy(closestExit);
                        }
                        // Disable other snaps if we are constrained to panel
                        snapped = true;

                        // ✅ SWAP LOGIC (Vertex Drag) - Using INDEX for reliable chain swapping
                        if (lastSnappedExitIndex.current !== -1 && closestExitIndex !== lastSnappedExitIndex.current) {
                            const draggedLineId = selectedGizmo.current.userData.lineId;
                            const panelId = currentPanelId.current;

                            // ✅ Get dragged wire's layer (bell, machine, etc.)
                            const draggedWireLayer = currentWireObj.current?.userData?.layer || null;
                            const isDraggedWireBell = draggedWireLayer === 'bell' ||
                                draggedLineId?.startsWith('wireBell') ||
                                currentWireObj.current?.userData?.type === 'wireBell';

                            // 🟢 Move Dragged Wire's Trafo - ONLY if dragging a bell wire

                            if (isDraggedWireBell && currentTrafo.current) {
                                updateBellTrafoPosition(currentTrafo.current, closestExit);
                            }
                            // Find other wire at closestExit (same layer only) - find CLOSEST match
                            let otherWire = null;
                            let minDistanceToClosestExit = Infinity; // ✅ Track closest match
                            scene.traverse(obj => {
                                if (obj.isLine && obj.userData.id !== draggedLineId) {
                                    // ✅ Skip snap markers and non-wire objects
                                    const wireId = obj.userData.id || '';
                                    if (wireId.startsWith('outSnap_') || wireId.startsWith('snap_')) {
                                        return; // Skip snap point markers
                                    }

                                    // ✅ Check panel connection with multiple possible property names
                                    const wirePanelId = obj.userData.panelID || obj.userData.panelId ||
                                        obj.userData.sourceId || obj.userData.sourceID;

                                    if (wirePanelId !== panelId) {
                                        return; // Skip wires not connected to this panel
                                    }

                                    // ✅ Layer check: only swap with same layer wires
                                    const otherWireLayer = obj.userData.layer;

                                    // Debug: Log all wires connected to this panel

                                    if (draggedWireLayer && otherWireLayer && otherWireLayer !== draggedWireLayer) {
                                        return; // Skip different layer wires
                                    }

                                    const pos = obj.geometry.attributes.position;
                                    const vertexCount = pos.count;
                                    const start = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                                    const end = new THREE.Vector3(pos.getX(vertexCount - 1), pos.getY(vertexCount - 1), pos.getZ(vertexCount - 1));

                                    // ✅ Check both start and end of wire (wire direction may vary)
                                    let distanceToExit = Math.min(start.distanceTo(closestExit), end.distanceTo(closestExit));
                                    let nearestEnd = start.distanceTo(closestExit) < end.distanceTo(closestExit) ? 'start' : 'end';

                                    // ✅ For bell wires only, also check trafo position
                                    const isBellWire = wireId.startsWith('wireBell') || obj.userData.layer === 'bell';
                                    if (isBellWire && obj.userData.branchID) {
                                        const trafo = findBellTrafoForBranch(scene, obj.userData.branchID);
                                        if (trafo) {
                                            const trafoPos = trafo.position.clone();
                                            const trafoDistance = trafoPos.distanceTo(closestExit);
                                            if (trafoDistance < distanceToExit) {
                                                distanceToExit = trafoDistance;
                                                nearestEnd = 'trafo';
                                            }
                                        }
                                    }

                                    // ✅ Check distance AND find CLOSEST wire (not just any match)
                                    if (distanceToExit < 8.0 && distanceToExit < minDistanceToClosestExit) {
                                        minDistanceToClosestExit = distanceToExit;
                                        otherWire = obj;
                                    }
                                }
                            });

                            if (otherWire) {
                                // ✅ Get target exit from array using INDEX (ensures correct swap chain)
                                const targetExit = connectedPanelExitPoints.current[lastSnappedExitIndex.current];

                                // Move otherWire to target exit (previous position in chain)
                                const offset = new THREE.Vector3().subVectors(targetExit, closestExit);

                                // Check for Trafo on otherWire - ONLY if otherWire is a bell wire
                                const isOtherWireBell = otherWire.userData.layer === 'bell' ||
                                    otherWire.userData.id?.startsWith('wireBell') ||
                                    otherWire.userData.type === 'wireBell';

                                if (isOtherWireBell && otherWire.userData.branchID) {
                                    const trafo = findBellTrafoForBranch(scene, otherWire.userData.branchID);
                                    if (trafo) {
                                        updateBellTrafoPosition(trafo, targetExit);
                                    }
                                }

                                const pos = otherWire.geometry.attributes.position;

                                const v0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0)).add(offset);
                                const v1 = new THREE.Vector3(pos.getX(1), pos.getY(1), pos.getZ(1)).add(offset);

                                pos.setXYZ(0, v0.x, v0.y, v0.z);
                                pos.setXYZ(1, v1.x, v1.y, v1.z);
                                pos.needsUpdate = true;
                                otherWire.geometry.computeBoundingSphere();
                            }

                            // ✅ Update both index and position for swap chain
                            lastSnappedExitIndex.current = closestExitIndex;
                            lastSnappedExitPoint.current = closestExit.clone();
                        }

                        // ✅ Log direction relative to exit points list
                        const exitPoints = connectedPanelExitPoints.current;
                        if (exitPoints.length > 1) {
                            const firstPoint = exitPoints[0];
                            const lastPoint = exitPoints[exitPoints.length - 1];

                            // Panel yönü (Baştan Sona)
                            const panelDir = new THREE.Vector3().subVectors(lastPoint, firstPoint).normalize();

                            // Sürükleme yönü (Mouse hareketi)
                            const dragDir = new THREE.Vector3().subVectors(gizmoTargetPosition, originalPosition.current);

                            const dot = dragDir.dot(panelDir);


                        }
                    }
                }
                // Case 2: Edge 0 (First Edge)
                else if (type === 'midpoint' && edgeIndex === 0) {
                    // Calculate where the start vertex would go with current movement
                    // Standard midpoint logic uses perpVector calculated from finalTarget
                    // We need to reverse engineer: find perpVector that lands start vertex on an exit point

                    const edgeStart = originalEdgePositions.current.start;
                    const edgeEnd = originalEdgePositions.current.end;
                    const edgeVector = new THREE.Vector3().subVectors(edgeEnd, edgeStart).normalize();

                    // Calculate standard perpVector based on mouse pos (finalTarget)
                    const gizmoMove = new THREE.Vector3().subVectors(finalTarget, originalPosition.current);
                    const parallelComponent = gizmoMove.dot(edgeVector);
                    let perpVector = gizmoMove.clone().sub(edgeVector.clone().multiplyScalar(parallelComponent));

                    // Predicted new start position
                    const predictedStart = edgeStart.clone().add(perpVector);

                    // Find closest exit point to predicted start
                    let closestExit = null;
                    let closestExitIndex = -1;
                    let minExitDist = Infinity;

                    connectedPanelExitPoints.current.forEach((ep, idx) => {
                        const d = predictedStart.distanceTo(ep);
                        if (d < minExitDist) {
                            minExitDist = d;
                            closestExit = ep;
                            closestExitIndex = idx;
                        }
                    });

                    if (closestExit) {
                        // Phase 2 (edge drag): Bundle aralığına swap engelle.
                        const _draggedLineIdEd = selectedGizmo.current?.userData?.lineId;
                        const _panelIdEd = currentPanelId.current;
                        if (_panelIdEd && _draggedLineIdEd) {
                            const _bundleEd = findBundleByPanelExitIndex(scene, _panelIdEd, closestExitIndex);
                            if (_bundleEd) {
                                const _wireIdsEd = _bundleEd.userData?.wireIds || [];
                                const _isMemberEd = _wireIdsEd.some(id => String(id) === String(_draggedLineIdEd));
                                if (!_isMemberEd) {
                                    closestExit = null;
                                    closestExitIndex = -1;
                                }
                            }
                        }
                    }

                    if (closestExit) {
                        // Force perpVector to align start with closestExit
                        // newStart = edgeStart + perpVector => perpVector = newStart - edgeStart
                        // But we must maintain perpendicularity? 
                        // User said: "iki çıkış varsa sürüklenen kenar diğer kenarın yerine çekebilmeli"
                        // This implies the edge moves to the other output.
                        // If we just set perpVector = closestExit - edgeStart, the edge moves there.
                        // It might not be strictly perpendicular to original edge anymore if outputs are not aligned with edge normal.
                        // But usually we want to move the edge.

                        // Let's override the finalTarget to produce the correct perpVector
                        // Actually, we can just modify perpVector later in the midpoint block.
                        // But finalTarget updates the gizmo position.

                        // Let's calculate the required offset
                        // ✅ Use shift from initialExitPoint to preserve trafo offsets
                        let requiredOffset;
                        if (initialExitPoint.current) {
                            requiredOffset = new THREE.Vector3().subVectors(closestExit, initialExitPoint.current);
                        } else {
                            requiredOffset = new THREE.Vector3().subVectors(closestExit, edgeStart);
                        }

                        // ✅ SWAP LOGIC (Edge Drag) - Using INDEX for reliable chain swapping
                        if (lastSnappedExitIndex.current !== -1 && closestExitIndex !== lastSnappedExitIndex.current) {
                            const draggedLineId = selectedGizmo.current.userData.lineId;
                            const panelId = currentPanelId.current;

                            // ✅ Get dragged wire's layer (bell, machine, etc.)
                            const draggedWireLayer = currentWireObj.current?.userData?.layer || null;
                            const isDraggedWireBell = draggedWireLayer === 'bell' ||
                                draggedLineId?.startsWith('wireBell') ||
                                currentWireObj.current?.userData?.type === 'wireBell';


                            if (isDraggedWireBell && currentTrafo.current) {
                                updateBellTrafoPosition(currentTrafo.current, closestExit);
                            }

                            // 2. Find other wire at closestExit (same layer only) - find CLOSEST match
                            let otherWire = null;
                            let minDistanceToClosestExit = Infinity; // ✅ Track closest match
                            scene.traverse(obj => {
                                if (obj.isLine && obj.userData.id !== draggedLineId) {
                                    // ✅ Skip snap markers and non-wire objects
                                    const wireId = obj.userData.id || '';
                                    if (wireId.startsWith('outSnap_') || wireId.startsWith('snap_')) {
                                        return; // Skip snap point markers
                                    }

                                    // ✅ Check panel connection with multiple possible property names
                                    const wirePanelId = obj.userData.panelID || obj.userData.panelId ||
                                        obj.userData.sourceId || obj.userData.sourceID;

                                    if (wirePanelId !== panelId) {
                                        return; // Skip wires not connected to this panel
                                    }

                                    // ✅ Layer check: only swap with same layer wires
                                    const otherWireLayer = obj.userData.layer;

                                    // Debug: Log all wires connected to this panel

                                    if (draggedWireLayer && otherWireLayer && otherWireLayer !== draggedWireLayer) {
                                        return; // Skip different layer wires
                                    }

                                    const pos = obj.geometry.attributes.position;
                                    const vertexCount = pos.count;
                                    const start = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                                    const end = new THREE.Vector3(pos.getX(vertexCount - 1), pos.getY(vertexCount - 1), pos.getZ(vertexCount - 1));

                                    // ✅ Check both start and end of wire (wire direction may vary)
                                    let distanceToExit = Math.min(start.distanceTo(closestExit), end.distanceTo(closestExit));
                                    let nearestEnd = start.distanceTo(closestExit) < end.distanceTo(closestExit) ? 'start' : 'end';

                                    // ✅ For bell wires only, also check trafo position
                                    const isBellWire = wireId.startsWith('wireBell') || obj.userData.layer === 'bell';
                                    if (isBellWire && obj.userData.branchID) {
                                        const trafo = findBellTrafoForBranch(scene, obj.userData.branchID);
                                        if (trafo) {
                                            const trafoPos = trafo.position.clone();
                                            const trafoDistance = trafoPos.distanceTo(closestExit);
                                            if (trafoDistance < distanceToExit) {
                                                distanceToExit = trafoDistance;
                                                nearestEnd = 'trafo';
                                            }
                                        }
                                    }

                                    // ✅ Check distance AND find CLOSEST wire (not just any match)
                                    if (distanceToExit < 8.0 && distanceToExit < minDistanceToClosestExit) {
                                        minDistanceToClosestExit = distanceToExit;
                                        otherWire = obj;
                                    }
                                }
                            });

                            if (otherWire) {
                                // ✅ Get target exit from array using INDEX (ensures correct swap chain)
                                const targetExit = connectedPanelExitPoints.current[lastSnappedExitIndex.current];
                                // Move otherWire to target exit (previous position in chain)
                                const offset = new THREE.Vector3().subVectors(targetExit, closestExit);

                                // Check for Trafo on otherWire - ONLY if otherWire is a bell wire
                                const isOtherWireBell = otherWire.userData.layer === 'bell' ||
                                    otherWire.userData.id?.startsWith('wireBell') ||
                                    otherWire.userData.type === 'wireBell';

                                if (isOtherWireBell && otherWire.userData.branchID) {
                                    const trafo = findBellTrafoForBranch(scene, otherWire.userData.branchID);
                                    if (trafo) {
                                        updateBellTrafoPosition(trafo, targetExit);
                                    }
                                }

                                const pos = otherWire.geometry.attributes.position;

                                const v0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0)).add(offset);
                                const v1 = new THREE.Vector3(pos.getX(1), pos.getY(1), pos.getZ(1)).add(offset);

                                pos.setXYZ(0, v0.x, v0.y, v0.z);
                                pos.setXYZ(1, v1.x, v1.y, v1.z);
                                pos.needsUpdate = true;
                                otherWire.geometry.computeBoundingSphere();
                            }

                            // ✅ Update both index and position for swap chain
                            lastSnappedExitIndex.current = closestExitIndex;
                            lastSnappedExitPoint.current = closestExit.clone();
                        }

                        // ✅ Log direction relative to exit points list (for edge dragging)
                        const exitPoints = connectedPanelExitPoints.current;
                        if (exitPoints.length > 1) {
                            const firstPoint = exitPoints[0];
                            const lastPoint = exitPoints[exitPoints.length - 1];

                            // Panel yönü (Baştan Sona)
                            const panelDir = new THREE.Vector3().subVectors(lastPoint, firstPoint).normalize();

                            // Sürükleme yönü (Mouse hareketi)
                            const dragDir = new THREE.Vector3().subVectors(gizmoTargetPosition, originalPosition.current);

                            const dot = dragDir.dot(panelDir);


                        }
                    }
                }
            }

            let delta;
            let perpVector = null; // Midpoint için perpendicular offset

            // ✅ Midpoint için özel pozisyon güncellemesi (paralel taşıma)
            if (type === 'midpoint') {
                // Orijinal kenarın yönünü bul
                const edgeStart = originalEdgePositions.current.start;
                const edgeEnd = originalEdgePositions.current.end;
                const edgeVector = new THREE.Vector3().subVectors(edgeEnd, edgeStart);
                edgeVector.normalize();

                // ✅ Edge 0 ve panel bağlantısı varsa, panel ekseni boyunca hareket et
                if (edgeIndex === 0 && connectedPanelExitPoints.current && connectedPanelExitPoints.current.length > 0 && initialExitPoint.current) {
                    // Closest exit point'i bul
                    let closestExit = null;
                    let minExitDist = Infinity;
                    connectedPanelExitPoints.current.forEach((ep) => {
                        const predicted = edgeStart.clone().add(new THREE.Vector3().subVectors(finalTarget, originalPosition.current));
                        const d = predicted.distanceTo(ep);
                        if (d < minExitDist) {
                            minExitDist = d;
                            closestExit = ep;
                        }
                    });

                    // Panel ekseni boyunca hareket et (Y-only değil, tam vektör)
                    if (closestExit) {
                        perpVector = new THREE.Vector3().subVectors(closestExit, initialExitPoint.current);
                    } else {
                        // Fallback: normal perpendicular hesaplama
                        const gizmoMove = new THREE.Vector3().subVectors(finalTarget, originalPosition.current);
                        const parallelComponent = gizmoMove.dot(edgeVector);
                        perpVector = gizmoMove.clone().sub(edgeVector.clone().multiplyScalar(parallelComponent));
                    }
                } else {
                    // Normal perpendicular offset hesapla
                    const gizmoMove = new THREE.Vector3().subVectors(finalTarget, originalPosition.current);
                    const parallelComponent = gizmoMove.dot(edgeVector);
                    perpVector = gizmoMove.clone().sub(edgeVector.clone().multiplyScalar(parallelComponent));
                }

                // ✅ Panel çıkış wire'ı: V0-V1 ilk kenar 10 birim altına düşemez.
                // Edge V1-V2 (edgeIndex=1) drag → V1 V0'a yaklaşıyor olabilir.
                // perpVector uygulansaydı V1 yeni nereye düşerdi, simüle et:
                // newV1 = rayIntersection(V0+prevDir, blockStart+perpVector+blockDir).
                // newV1'in V0'a olan prevDir-projeksiyonu < 10 ise hem perpVector hem
                // gizmo'yu son geçerli konuma DONDUR (sürükleme kilitlenir, devam etmez).
                if (perpVector && type === 'midpoint' && edgeIndex === 1 &&
                    currentWireObj.current?.userData?.panelID &&
                    !currentWireObj.current.userData?.isBundleRep &&
                    currentWireObj.current.userData?.sourceType !== 'socketBuat' &&
                    currentWireObj.current.userData?.sourceType !== 'socketVertex') {
                    const _posAttr = currentWireObj.current.geometry?.attributes?.position;
                    if (_posAttr && _posAttr.count >= 3) {
                        const _v0 = new THREE.Vector3(_posAttr.getX(0), _posAttr.getY(0), _posAttr.getZ(0));
                        const _v1 = new THREE.Vector3(_posAttr.getX(1), _posAttr.getY(1), _posAttr.getZ(1));
                        const _v2 = new THREE.Vector3(_posAttr.getX(2), _posAttr.getY(2), _posAttr.getZ(2));
                        const _prevDir = new THREE.Vector3().subVectors(_v1, _v0).normalize();
                        const _blockDir = new THREE.Vector3().subVectors(_v2, _v1).normalize();
                        const _newBlockStart = _v1.clone().add(perpVector);
                        const _intersection = rayIntersection2D(_v0, _prevDir, _newBlockStart, _blockDir);
                        if (_intersection) {
                            const _proj = new THREE.Vector3().subVectors(_intersection, _v0).dot(_prevDir);
                            if (_proj < 10) {
                                // Sınırı aştı: son geçerli perpVector'ü kullan, gizmo'yu da o
                                // konuma sabitle. Yoksa zero vector — gizmo originalPosition'da kalır.
                                if (lastValidMidpointPerp.current) {
                                    perpVector.copy(lastValidMidpointPerp.current);
                                } else {
                                    perpVector.set(0, 0, 0);
                                }
                            } else {
                                if (!lastValidMidpointPerp.current) lastValidMidpointPerp.current = new THREE.Vector3();
                                lastValidMidpointPerp.current.copy(perpVector);
                            }
                        }
                    }
                }

                // ✅ Bundle slice constraint: ilk/son kenar slice dışına çıkmasın
                if (perpVector && currentWireObj.current?.userData?.isBundleRep) {
                    const posCount = currentWireObj.current.geometry?.attributes?.position?.count ?? 0;
                    // Entry kısıtı: kenarin BAŞLANGIÇ ucuna (vertex 0, entry tarafı) göre hesapla.
                    // Exit kısıtı: kenarin BİTİŞ ucuna (son vertex, exit tarafı) göre hesapla.
                    // Böylece L-şekli gibi durumlarda iç köşe, yanlış kısıt hesabını engellemez.
                    // posCount=2 (tek kenarlı rep line) durumunda aynı kenar her ikisine de tabidir.
                    const bcs = [];
                    if (edgeIndex === 0 && currentWireObj.current.userData._bundleConstraintEntry)
                        bcs.push({ bc: currentWireObj.current.userData._bundleConstraintEntry, useStart: true });
                    if (posCount >= 2 && edgeIndex === posCount - 2 && currentWireObj.current.userData._bundleConstraintExit)
                        bcs.push({ bc: currentWireObj.current.userData._bundleConstraintExit, useStart: false });
                    for (const { bc, useStart } of bcs) {
                        const bcPerp = new THREE.Vector3(bc.perpX, bc.perpY, 0);
                        const perpComp = perpVector.dot(bcPerp);
                        const refVertex = useStart ? originalEdgePositions.current.start : originalEdgePositions.current.end;
                        const currentPerp = refVertex.dot(bcPerp);
                        const clampedComp = Math.max(bc.perpMin - currentPerp, Math.min(bc.perpMax - currentPerp, perpComp));
                        if (Math.abs(clampedComp - perpComp) > 0.001) {
                            perpVector.addScaledVector(bcPerp, clampedComp - perpComp);
                        }
                    }
                }

                // Gizmo pozisyonunu perpendicular offset'e göre güncelle
                selectedGizmo.current.position.copy(originalPosition.current.clone().add(perpVector));

                // Delta olarak perpendicular offset'i kullan
                delta = perpVector;

                // ✅ Socketleri de taşı
                if (originalSockets.current && originalSockets.current.length > 0) {
                    // Kenar parametreleri
                    const edgeStart = originalEdgePositions.current.start;
                    const edgeEnd = originalEdgePositions.current.end;

                    // Yeni kenar pozisyonları (perpVector eklenmiş)
                    const newStart = edgeStart.clone().add(perpVector);
                    const newEnd = edgeEnd.clone().add(perpVector);

                    originalSockets.current.forEach(socketData => {
                        if (socketData.isWireMarker) {
                            // ✅ Wire marker: updateGizmos'da güncellenir (geometri güncellemesi gerekli)
                            // Şimdilik atla
                        } else {
                            // Normal komponentler: perpVector ekle
                            const newPos = socketData.originalPosition.clone().add(perpVector);
                            socketData.object.position.copy(newPos);

                            // ✅ Eğer bu buat ise ve fixture wire'ları varsa, onları da sürükle
                            const obj = socketData.object;
                            if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                obj.userData?.fixtureWireMapIds && obj.userData.fixtureWireMapIds.length > 0) {

                                obj.userData.fixtureWireMapIds.forEach(fixtureWireId => {
                                    // Fixture wire'ı sahnede bul
                                    scene.traverse(fixtureObj => {
                                        if (fixtureObj.isLine &&
                                            fixtureObj.userData?.id === fixtureWireId &&
                                            fixtureObj.userData?.function === 'wireLightingFixture') {

                                            const fixturePos = fixtureObj.geometry.attributes.position;

                                            // ✅ İlk vertex'i güncelle
                                            fixturePos.setXYZ(0, newPos.x, newPos.y, newPos.z);

                                            // ✅ userData vertices'i de güncelle
                                            if (fixtureObj.userData.vertices && fixtureObj.userData.vertices.length > 0) {
                                                fixtureObj.userData.vertices[0] = {
                                                    x: newPos.x,
                                                    y: newPos.y,
                                                    z: newPos.z
                                                };

                                                // ✅ Spline'ı yeniden oluştur
                                                const controlPoints = fixtureObj.userData.vertices.map(v =>
                                                    new THREE.Vector3(v.x, v.y, v.z || 0)
                                                );
                                                const curve = new THREE.CatmullRomCurve3(controlPoints);
                                                const divisions = Math.max(10, controlPoints.length * 10);
                                                const splinePoints = curve.getPoints(divisions);

                                                // Geometriyi güncelle
                                                fixtureObj.geometry.dispose();
                                                fixtureObj.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
                                            }

                                            fixturePos.needsUpdate = true;
                                        }
                                    });
                                });
                            }

                            // ✅ Eğer bu buat ise ve lighting wire'ları varsa, onları da sürükle (ilk 2 vertex)
                            if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                obj.userData?.lightingWireMapIds && obj.userData.lightingWireMapIds.length > 0) {

                                obj.userData.lightingWireMapIds.forEach(lightingWireId => {
                                    // Lighting wire'ı sahnede bul
                                    scene.traverse(lightingObj => {
                                        if (lightingObj.isLine &&
                                            lightingObj.userData?.id === lightingWireId &&
                                            (lightingObj.userData?.function === 'wireLighting' ||
                                                lightingObj.userData?.function === 'simpleLighting')) {

                                            const lightingPos = lightingObj.geometry.attributes.position;

                                            // İlk iki vertex'i bul
                                            const firstVertex = new THREE.Vector3(
                                                lightingPos.getX(0),
                                                lightingPos.getY(0),
                                                lightingPos.getZ(0)
                                            );
                                            const secondVertex = new THREE.Vector3(
                                                lightingPos.getX(1),
                                                lightingPos.getY(1),
                                                lightingPos.getZ(1)
                                            );

                                            // İki vertex arasındaki orijinal mesafe ve yön
                                            const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);

                                            // ✅ İlk vertex'i buat'ın yeni pozisyonuna taşı
                                            lightingPos.setXYZ(0, newPos.x, newPos.y, newPos.z);

                                            // ✅ İkinci vertex'i de aynı delta ile hareket ettir (oranı koru)
                                            const newSecondVertex = newPos.clone().add(offset);
                                            lightingPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);

                                            // ✅ userData vertices'i de güncelle
                                            if (lightingObj.userData.vertices && lightingObj.userData.vertices.length >= 2) {
                                                lightingObj.userData.vertices[0] = {
                                                    x: newPos.x,
                                                    y: newPos.y,
                                                    z: newPos.z
                                                };
                                                lightingObj.userData.vertices[1] = {
                                                    x: newSecondVertex.x,
                                                    y: newSecondVertex.y,
                                                    z: newSecondVertex.z
                                                };
                                            }

                                            lightingPos.needsUpdate = true;
                                        }
                                    });
                                });
                            }

                            // ✅ Eğer bu buat ise ve socket wire'ları varsa, onları da sürükle (ilk 2 vertex)
                            if (obj.userData?.type === 'socketBuat' &&
                                obj.userData?.socketWireMapIds && obj.userData.socketWireMapIds.length > 0) {

                                obj.userData.socketWireMapIds.forEach(socketWireId => {
                                    // Socket wire'ı sahnede bul
                                    scene.traverse(socketObj => {
                                        if (socketObj.isLine &&
                                            socketObj.userData?.id === socketWireId &&
                                            (socketObj.userData?.function === 'socket' ||
                                                socketObj.userData?.function === 'simpleSocket')) {

                                            const socketPos = socketObj.geometry.attributes.position;
                                            const vertexCount = socketPos.count;

                                            // İlk iki vertex'i bul
                                            const firstVertex = new THREE.Vector3(
                                                socketPos.getX(0),
                                                socketPos.getY(0),
                                                socketPos.getZ(0)
                                            );
                                            const secondVertex = new THREE.Vector3(
                                                socketPos.getX(1),
                                                socketPos.getY(1),
                                                socketPos.getZ(1)
                                            );

                                            // İki vertex arasındaki orijinal mesafe ve yön
                                            const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);

                                            // ✅ İlk vertex'i her zaman buat'ın yeni pozisyonuna taşı
                                            socketPos.setXYZ(0, newPos.x, newPos.y, newPos.z);

                                            // ✅ Paralel yapıyı korumak için hareket yönünü analiz et
                                            if (vertexCount >= 3) {
                                                // İkinci kenarın yönünü bul (vertex 1 → vertex 2)
                                                const thirdVertex = new THREE.Vector3(
                                                    socketPos.getX(2),
                                                    socketPos.getY(2),
                                                    socketPos.getZ(2)
                                                );
                                                const secondEdgeDir = new THREE.Vector3().subVectors(thirdVertex, secondVertex).normalize();

                                                // Buat'ın hareket yönü (perpVector)
                                                const movementDir = perpVector.clone().normalize();

                                                // Hareket yönü ile ikinci kenarın dot product'ını hesapla
                                                const dotProduct = Math.abs(movementDir.dot(secondEdgeDir));

                                                // Eğer dot product yüksekse (>0.7) → Paralel hareket, vertex 1 de hareket eder
                                                // Eğer dot product düşükse (<0.7) → Dik hareket, vertex 1 sabit kalır
                                                if (dotProduct > 0.7) {
                                                    // Paralel hareket: İkinci vertex'i de aynı delta ile hareket ettir
                                                    const newSecondVertex = newPos.clone().add(offset);
                                                    socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);

                                                    if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                        socketObj.userData.vertices[1] = {
                                                            x: newSecondVertex.x,
                                                            y: newSecondVertex.y,
                                                            z: newSecondVertex.z
                                                        };
                                                    }
                                                }
                                                // Else: Dik hareket - vertex 1 sabit kalır (güncelleme yapma)
                                            } else {
                                                // Sadece 2 vertex var, normal güncelle
                                                const newSecondVertex = newPos.clone().add(offset);
                                                socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);

                                                if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                    socketObj.userData.vertices[1] = {
                                                        x: newSecondVertex.x,
                                                        y: newSecondVertex.y,
                                                        z: newSecondVertex.z
                                                    };
                                                }
                                            }

                                            // ✅ userData vertices'i de güncelle (vertex 0)
                                            if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 1) {
                                                socketObj.userData.vertices[0] = {
                                                    x: newPos.x,
                                                    y: newPos.y,
                                                    z: newPos.z
                                                };
                                            }

                                            socketPos.needsUpdate = true;
                                        }
                                    });
                                });
                            }
                        }
                    });
                } else {
                    // console.warn("Gizmo Move: No objects to move in originalSockets");
                }
            } else if (type === 'vertex') {
                // ✅ Bundle slice constraint: vertex drag'de ilk/son köşe slice dışına çıkmasın
                if (currentWireObj.current?.userData?.isBundleRep) {
                    const posCount = currentWireObj.current.geometry?.attributes?.position?.count ?? 0;
                    let bc = null;
                    if (vertexIndex === 0)
                        bc = currentWireObj.current.userData._bundleConstraintEntry;
                    else if (posCount >= 1 && vertexIndex === posCount - 1)
                        bc = currentWireObj.current.userData._bundleConstraintExit;
                    if (bc) {
                        const bcPerp = new THREE.Vector3(bc.perpX, bc.perpY, 0);
                        const currentPerp = finalTarget.dot(bcPerp);
                        const clampedPerp = Math.max(bc.perpMin, Math.min(bc.perpMax, currentPerp));
                        if (Math.abs(clampedPerp - currentPerp) > 0.001) {
                            finalTarget.addScaledVector(bcPerp, clampedPerp - currentPerp);
                        }
                    }
                }
                // Vertex gizmo pozisyonu ray intersection sonrasında güncelleniyor
                // Burada sadece delta hesapla
                delta = new THREE.Vector3().subVectors(finalTarget, selectedGizmo.current.position);
            } else {
                // Diğer gizmo tipleri için normal pozisyon güncellemesi
                const oldPosition = selectedGizmo.current.position.clone();
                selectedGizmo.current.position.copy(finalTarget);
                delta = new THREE.Vector3().subVectors(finalTarget, oldPosition);
            }

            scene.traverse((obj) => {
                if (obj.isLine && obj.userData.id === lineId) {
                    const pos = obj.geometry.attributes.position;
                    const positions = pos.array;

                    if (type === 'vertex') {
                        // ✅ Köşe sürüklendiğinde, her iki taraftaki kenarı ofsetle (açıları koru)
                        const data = originalVertexData.current;
                        if (!data) return;

                        // ✅ Grid mode: mouse hareketini orijinal vertex'e göre göreceli grid'e snap et
                        let snappedTarget = finalTarget.clone();
                        if (gridMode) {
                            const gridSize = 5;
                            const relativeMove = new THREE.Vector3().subVectors(finalTarget, data.vertex);
                            relativeMove.x = Math.round(relativeMove.x / gridSize) * gridSize;
                            relativeMove.y = Math.round(relativeMove.y / gridSize) * gridSize;
                            relativeMove.z = Math.round(relativeMove.z / gridSize) * gridSize;
                            snappedTarget = data.vertex.clone().add(relativeMove);
                        }

                        const movement = new THREE.Vector3().subVectors(snappedTarget, data.vertex);

                        // ✅ COLLINEAR CHAIN LOGIC
                        // If this vertex is part of a collinear chain, move the whole chain perpendicularly
                        if (data.collinearChain) {
                            const chain = data.collinearChain;
                            const startV = chain.vertices[0].pos;
                            const endV = chain.vertices[chain.vertices.length - 1].pos;
                            const chainDir = new THREE.Vector3().subVectors(endV, startV).normalize();

                            // Decompose movement
                            const parallelComp = movement.dot(chainDir);
                            const perpVector = movement.clone().sub(chainDir.clone().multiplyScalar(parallelComp));
                            const parallelVector = chainDir.clone().multiplyScalar(parallelComp);

                            // Apply perpendicular movement to ALL vertices in the chain
                            chain.vertices.forEach(v => {
                                const newPos = v.pos.clone().add(perpVector);

                                // Apply parallel movement ONLY to the dragged vertex
                                // And interpolate others?
                                // If we drag vertex 4 (end of chain), we stretch the chain.
                                // If we drag vertex 2 (start of chain), we stretch the chain.
                                // If we drag vertex 3 (middle), we break the chain? No, usually we just move 3.

                                // Logic:
                                // 1. Base position = Original + PerpVector
                                // 2. Add ParallelVector based on which vertex is dragged.

                                if (v.index === vertexIndex) {
                                    // This is the dragged vertex. Add full parallel component.
                                    newPos.add(parallelVector);
                                } else {
                                    // This is another vertex in the chain.
                                    // Should it move parallel?
                                    // If we drag the END, the START should stay fixed (parallel-wise).
                                    // If we drag the START, the END should stay fixed.
                                    // If we drag the MIDDLE, maybe both ends stay fixed?

                                    // For now, keep others fixed in parallel direction (only perp move).
                                    // This effectively stretches the segment connected to the dragged vertex.
                                }

                                pos.setXYZ(v.index, newPos.x, newPos.y, newPos.z);

                                // Update Gizmo if this is the dragged vertex
                                if (v.index === vertexIndex) {
                                    selectedGizmo.current.position.copy(newPos);
                                }
                            });

                            // Interpolate internal vertices if any (to keep them evenly spaced or proportional)
                            // Actually, if we only move the dragged vertex parallel, the internal vertices might need adjustment
                            // if they are supposed to stay proportional (e.g. components).
                            // But usually components are anchored to t-values on edges.
                            // If we stretch an edge, the component moves with t.
                            // Here we are moving vertices.
                            // If 2-3-4. Drag 4. 3 is at index 2. 4 is at index 3.
                            // 3 stays at original parallel pos. 4 moves parallel.
                            // Segment 3-4 stretches. Segment 2-3 stays same length.
                            // This is correct for "Vertex Drag".

                            // Update Components on the chain
                            if (originalSockets.current && originalSockets.current.length > 0) {
                                originalSockets.current.forEach(socketData => {
                                    // Find the edge this socket belongs to
                                    const edgeIdx = socketData.edgeIndex;

                                    // Get current positions of edge endpoints
                                    const s = new THREE.Vector3(pos.getX(edgeIdx), pos.getY(edgeIdx), pos.getZ(edgeIdx));
                                    const e = new THREE.Vector3(pos.getX(edgeIdx + 1), pos.getY(edgeIdx + 1), pos.getZ(edgeIdx + 1));

                                    if (socketData.isWireMarker) {
                                        const newPos = new THREE.Vector3().lerpVectors(s, e, 0.5);
                                        socketData.object.position.copy(newPos);
                                        const edgeDir = new THREE.Vector3().subVectors(e, s);
                                        const angle = Math.atan2(edgeDir.y, edgeDir.x);
                                        const angleRad = THREE.MathUtils.degToRad(45);
                                        socketData.object.rotation.z = angle + angleRad;
                                    } else {
                                        const newPos = new THREE.Vector3().lerpVectors(s, e, socketData.t);
                                        socketData.object.position.copy(newPos);
                                    }
                                });
                            }

                            // Handle neighbors outside the chain (to maintain connections)
                            // The chain vertices are already updated.
                            // We just need to ensure the segments connecting to the chain are valid.
                            // Since we updated the vertices in the buffer, the segments (lines) are automatically updated.
                            // But we might need to check for intersections with previous/next segments if they exist?
                            // Standard vertex dragging does intersection checks.
                            // Here we are doing "Rigid Chain Move".
                            // If the chain moves perpendicularly, the segments connecting to the chain start/end will stretch/rotate.
                            // This is desired.

                            pos.needsUpdate = true; // ✅ Ensure update before return
                            return; // Skip standard logic
                        }

                        const hasLeft = data.leftNeighbor !== null;
                        const hasRight = data.rightNeighbor !== null;

                        // Retrieve chains from onMouseDown
                        const leftChain = originalVertexData.current.leftCollinearChain;
                        const rightChain = originalVertexData.current.rightCollinearChain;

                        let newVertex = snappedTarget.clone();

                        // Calculate Offsets (Rigid Move)
                        let leftOffset = new THREE.Vector3();
                        let rightOffset = new THREE.Vector3();
                        let leftDir = null;
                        let rightDir = null;

                        if (hasLeft) {
                            leftDir = new THREE.Vector3().subVectors(data.vertex, data.leftNeighbor).normalize();
                            const leftPerp = new THREE.Vector3(-leftDir.y, leftDir.x, 0);
                            let leftOffsetAmount = movement.dot(leftPerp);
                            // ✅ Bundle rep: slice sınırını açı koruyarak uygula
                            // leftOffset'i kısıtlayarak vertex0 (entry) slice dışına çıkmasın
                            if (obj.userData?.isBundleRep) {
                                const leftFarIdx = leftChain ? leftChain.vertices[0].index : vertexIndex - 1;
                                if (leftFarIdx === 0 && obj.userData._bundleConstraintEntry) {
                                    const bc = obj.userData._bundleConstraintEntry;
                                    const bcPerp = new THREE.Vector3(bc.perpX, bc.perpY, 0);
                                    const leftFarPos = leftChain ? leftChain.vertices[0].pos : data.leftNeighbor;
                                    const dotLPBC = leftPerp.dot(bcPerp);
                                    if (Math.abs(dotLPBC) > 0.001) {
                                        const v0p = leftFarPos.dot(bcPerp);
                                        const newPerp = v0p + leftOffsetAmount * dotLPBC;
                                        const clampedPerp = Math.max(bc.perpMin, Math.min(bc.perpMax, newPerp));
                                        if (Math.abs(clampedPerp - newPerp) > 0.001) {
                                            leftOffsetAmount = (clampedPerp - v0p) / dotLPBC;
                                        }
                                    }
                                }
                            }
                            leftOffset.copy(leftPerp).multiplyScalar(leftOffsetAmount);
                        }

                        if (hasRight) {
                            rightDir = new THREE.Vector3().subVectors(data.rightNeighbor, data.vertex).normalize();
                            const rightPerp = new THREE.Vector3(-rightDir.y, rightDir.x, 0);
                            let rightOffsetAmount = movement.dot(rightPerp);
                            // ✅ Bundle rep: slice sınırını açı koruyarak uygula
                            // rightOffset'i kısıtlayarak son vertex (exit) slice dışına çıkmasın
                            if (obj.userData?.isBundleRep) {
                                const rightFarIdx = rightChain
                                    ? rightChain.vertices[rightChain.vertices.length - 1].index
                                    : vertexIndex + 1;
                                if (rightFarIdx === pos.count - 1 && obj.userData._bundleConstraintExit) {
                                    const bc = obj.userData._bundleConstraintExit;
                                    const bcPerp = new THREE.Vector3(bc.perpX, bc.perpY, 0);
                                    const rightFarPos = rightChain
                                        ? rightChain.vertices[rightChain.vertices.length - 1].pos
                                        : data.rightNeighbor;
                                    const dotRPBC = rightPerp.dot(bcPerp);
                                    if (Math.abs(dotRPBC) > 0.001) {
                                        const vNp = rightFarPos.dot(bcPerp);
                                        const newPerp = vNp + rightOffsetAmount * dotRPBC;
                                        const clampedPerp = Math.max(bc.perpMin, Math.min(bc.perpMax, newPerp));
                                        if (Math.abs(clampedPerp - newPerp) > 0.001) {
                                            rightOffsetAmount = (clampedPerp - vNp) / dotRPBC;
                                        }
                                    }
                                }
                            }
                            rightOffset.copy(rightPerp).multiplyScalar(rightOffsetAmount);
                        }

                        // Determine newVertex position (Intersection)
                        if (hasLeft && hasRight) {
                            // Check if they are collinear (angle ~ 0)
                            // leftDir is (vertex - leftNeighbor), rightDir is (rightNeighbor - vertex)
                            // If straight line, they point in same direction.
                            const angle = leftDir.angleTo(rightDir);

                            if (angle > 0.1) { // Not collinear
                                const newLeftLinePoint = data.vertex.clone().add(leftOffset);
                                const newRightLinePoint = data.vertex.clone().add(rightOffset);
                                const intersection = rayIntersection2D(newLeftLinePoint, leftDir, newRightLinePoint, rightDir);
                                if (intersection) {
                                    newVertex = intersection;
                                }
                            }
                        }

                        // İlk/son kenar 10 birim altına düşecekse güncellemeyi durdur:
                        //  - Bundle rep: hem ilk (V0-V1) hem son (Vn-1-Vn) kenar 10+
                        //  - Panel çıkış wire'ı: sadece ilk kenar 10+ (V0 panel exit point'e
                        //    pinli, çok kısalırsa görsel bozulur). Branch wire'lar (sourceType
                        //    socketBuat/socketVertex) hariç — onların V0'ı junction'da.
                        const _ud = obj.userData || {};
                        const _isBundleRep = !!_ud.isBundleRep;
                        const _isPanelExit = !_isBundleRep && !!_ud.panelID &&
                            _ud.sourceType !== 'socketBuat' && _ud.sourceType !== 'socketVertex';
                        const _checkFirst = _isBundleRep || _isPanelExit;
                        const _checkLast = _isBundleRep;
                        if (_checkFirst || _checkLast) {
                            const MIN_LEN = 10;
                            let blockUpdate = false;

                            if (_checkFirst && hasLeft && leftDir) {
                                const leftFarIdx = leftChain ? leftChain.vertices[0].index : vertexIndex - 1;
                                const leftFarPos = leftChain ? leftChain.vertices[0].pos : data.leftNeighbor;
                                if (leftFarIdx === 0) {
                                    // V1 sürükleniyor: yeni V1 ile V0 arası mesafeyi kontrol et
                                    if (newVertex.dot(leftDir) - leftFarPos.dot(leftDir) < MIN_LEN)
                                        blockUpdate = true;
                                } else if (leftFarIdx === 1 && !leftChain && data.leftLeftNeighbor) {
                                    // V2 sürükleniyor: V1'in yeni konumunu ön-hesapla, V0-V1 kenarını kontrol et
                                    const tentativeFarEnd = leftFarPos.clone().add(leftOffset);
                                    const prevDir2 = new THREE.Vector3().subVectors(leftFarPos, data.leftLeftNeighbor).normalize();
                                    const v1New = rayIntersection2D(data.leftLeftNeighbor, prevDir2, tentativeFarEnd, leftDir);
                                    if (v1New) {
                                        const edgeLen = new THREE.Vector3().subVectors(v1New, data.leftLeftNeighbor).dot(prevDir2);
                                        if (edgeLen < MIN_LEN) blockUpdate = true;
                                    }
                                }
                            }

                            if (_checkLast && !blockUpdate && hasRight && rightDir) {
                                const rightFarIdx = rightChain
                                    ? rightChain.vertices[rightChain.vertices.length - 1].index
                                    : vertexIndex + 1;
                                const rightFarPos = rightChain
                                    ? rightChain.vertices[rightChain.vertices.length - 1].pos
                                    : data.rightNeighbor;
                                if (rightFarIdx === pos.count - 1) {
                                    // Vn-1 sürükleniyor: Vn-1 ile Vn arası mesafeyi kontrol et
                                    if (rightFarPos.dot(rightDir) - newVertex.dot(rightDir) < MIN_LEN)
                                        blockUpdate = true;
                                } else if (rightFarIdx === pos.count - 2 && !rightChain && data.rightRightNeighbor) {
                                    // Vn-2 sürükleniyor: Vn-1'in yeni konumunu ön-hesapla, Vn-1-Vn kenarını kontrol et
                                    const tentativeFarEnd = rightFarPos.clone().add(rightOffset);
                                    const nextDir2 = new THREE.Vector3().subVectors(data.rightRightNeighbor, rightFarPos).normalize();
                                    const vn1New = rayIntersection2D(data.rightRightNeighbor, nextDir2, tentativeFarEnd, rightDir);
                                    if (vn1New) {
                                        const edgeLen = new THREE.Vector3().subVectors(data.rightRightNeighbor, vn1New).dot(nextDir2);
                                        if (edgeLen < MIN_LEN) blockUpdate = true;
                                    }
                                }
                            }

                            if (blockUpdate) return;
                        }

                        // Update Vertex
                        pos.setXYZ(vertexIndex, newVertex.x, newVertex.y, newVertex.z);
                        selectedGizmo.current.position.copy(newVertex);

                        // Update Left Side
                        if (hasLeft) {
                            let farEndVertexPos, farEndIndex;
                            let prevFixedVertexPos = null;

                            if (leftChain) {
                                farEndIndex = leftChain.vertices[0].index;
                                farEndVertexPos = leftChain.vertices[0].pos.clone();
                                if (leftChain.startEdgeIdx > 0) {
                                    // Get vertex before chain
                                    prevFixedVertexPos = new THREE.Vector3(pos.getX(leftChain.startEdgeIdx - 1), pos.getY(leftChain.startEdgeIdx - 1), pos.getZ(leftChain.startEdgeIdx - 1));
                                }
                            } else {
                                farEndIndex = vertexIndex - 1;
                                farEndVertexPos = data.leftNeighbor.clone();
                                if (data.leftLeftNeighbor) {
                                    prevFixedVertexPos = data.leftLeftNeighbor.clone();
                                }
                            }

                            // Calculate where the far end wants to go
                            let finalFarEnd = farEndVertexPos.clone().add(leftOffset);

                            // Intersect with previous fixed segment if exists
                            if (prevFixedVertexPos) {
                                const prevDir = new THREE.Vector3().subVectors(farEndVertexPos, prevFixedVertexPos).normalize();
                                const intersection = rayIntersection2D(prevFixedVertexPos, prevDir, finalFarEnd, leftDir);
                                if (intersection) {
                                    finalFarEnd = intersection;
                                }
                            }

                            // Apply to chain or single neighbor
                            if (leftChain) {
                                const actualDelta = new THREE.Vector3().subVectors(finalFarEnd, farEndVertexPos);
                                leftChain.vertices.forEach(v => {
                                    if (v.index !== vertexIndex) {
                                        const newP = v.pos.clone().add(actualDelta);
                                        pos.setXYZ(v.index, newP.x, newP.y, newP.z);
                                    }
                                });
                            } else {
                                pos.setXYZ(farEndIndex, finalFarEnd.x, finalFarEnd.y, finalFarEnd.z);
                            }
                        }

                        // Update Right Side
                        if (hasRight) {
                            let farEndVertexPos, farEndIndex;
                            let nextFixedVertexPos = null;

                            if (rightChain) {
                                const lastV = rightChain.vertices[rightChain.vertices.length - 1];
                                farEndIndex = lastV.index;
                                farEndVertexPos = lastV.pos.clone();

                                if (rightChain.endEdgeIdx < pos.count - 2) {
                                    nextFixedVertexPos = new THREE.Vector3(pos.getX(rightChain.endEdgeIdx + 2), pos.getY(rightChain.endEdgeIdx + 2), pos.getZ(rightChain.endEdgeIdx + 2));
                                }
                            } else {
                                farEndIndex = vertexIndex + 1;
                                farEndVertexPos = data.rightNeighbor.clone();
                                if (data.rightRightNeighbor) {
                                    nextFixedVertexPos = data.rightRightNeighbor.clone();
                                }
                            }

                            // Calculate where the far end wants to go
                            let finalFarEnd = farEndVertexPos.clone().add(rightOffset);

                            // Intersect with next fixed segment if exists
                            if (nextFixedVertexPos) {
                                const nextDir = new THREE.Vector3().subVectors(nextFixedVertexPos, farEndVertexPos).normalize();
                                const intersection = rayIntersection2D(nextFixedVertexPos, nextDir, finalFarEnd, rightDir);
                                if (intersection) {
                                    finalFarEnd = intersection;
                                }
                            }

                            // Apply
                            if (rightChain) {
                                const actualDelta = new THREE.Vector3().subVectors(finalFarEnd, farEndVertexPos);
                                rightChain.vertices.forEach(v => {
                                    if (v.index !== vertexIndex) {
                                        const newP = v.pos.clone().add(actualDelta);
                                        pos.setXYZ(v.index, newP.x, newP.y, newP.z);
                                    }
                                });
                            } else {
                                pos.setXYZ(farEndIndex, finalFarEnd.x, finalFarEnd.y, finalFarEnd.z);
                            }
                        }

                        // ✅ Komponentleri yeni kenar pozisyonlarına taşı
                        if (originalSockets.current && originalSockets.current.length > 0) {
                            originalSockets.current.forEach(socketData => {
                                // Get current positions of the edge vertices from the updated geometry
                                const idx = socketData.edgeIndex;
                                const vStart = new THREE.Vector3(pos.getX(idx), pos.getY(idx), pos.getZ(idx));
                                const vEnd = new THREE.Vector3(pos.getX(idx + 1), pos.getY(idx + 1), pos.getZ(idx + 1));

                                if (socketData.isWireMarker) {
                                    // Wire marker logic (handled elsewhere or just keep centered?)
                                    // Usually wire markers are updated in updateGizmos, but if we want them to move smoothly:
                                    const newPos = new THREE.Vector3().lerpVectors(vStart, vEnd, 0.5);
                                    socketData.object.position.copy(newPos);
                                } else {
                                    const newPos = new THREE.Vector3().lerpVectors(vStart, vEnd, socketData.t);
                                    socketData.object.position.copy(newPos);

                                    // ✅ Eğer bu buat ise ve fixture wire'ları varsa, onları da sürükle
                                    const obj = socketData.object;
                                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                        obj.userData?.fixtureWireMapIds && obj.userData.fixtureWireMapIds.length > 0) {

                                        obj.userData.fixtureWireMapIds.forEach(fixtureWireId => {
                                            // Fixture wire'ı sahnede bul (cached)
                                            const fixtureObj = _findById(fixtureWireId);
                                            if (fixtureObj && fixtureObj.isLine &&
                                                fixtureObj.userData?.function === 'wireLightingFixture') {
                                                const fixturePos = fixtureObj.geometry.attributes.position;
                                                fixturePos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                if (fixtureObj.userData.vertices && fixtureObj.userData.vertices.length > 0) {
                                                    fixtureObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                    const controlPoints = fixtureObj.userData.vertices.map(v =>
                                                        new THREE.Vector3(v.x, v.y, v.z || 0)
                                                    );
                                                    const curve = new THREE.CatmullRomCurve3(controlPoints);
                                                    const divisions = Math.max(10, controlPoints.length * 10);
                                                    const splinePoints = curve.getPoints(divisions);
                                                    fixtureObj.geometry.dispose();
                                                    fixtureObj.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
                                                }
                                                fixturePos.needsUpdate = true;
                                            }
                                        });
                                    }

                                    // ✅ Eğer bu buat ise ve lighting wire'ları varsa, onları da sürükle (ilk 2 vertex)
                                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                        obj.userData?.lightingWireMapIds && obj.userData.lightingWireMapIds.length > 0) {

                                        obj.userData.lightingWireMapIds.forEach(lightingWireId => {
                                            // Lighting wire'ı sahnede bul
                                            const lightingObj = _findById(lightingWireId);
                                            if (lightingObj && lightingObj.isLine &&
                                                (lightingObj.userData?.function === 'wireLighting' ||
                                                 lightingObj.userData?.function === 'simpleLighting')) {
                                                const lightingPos = lightingObj.geometry.attributes.position;
                                                const firstVertex = new THREE.Vector3(lightingPos.getX(0), lightingPos.getY(0), lightingPos.getZ(0));
                                                const secondVertex = new THREE.Vector3(lightingPos.getX(1), lightingPos.getY(1), lightingPos.getZ(1));
                                                const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);
                                                lightingPos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                const newSecondVertex = newPos.clone().add(offset);
                                                lightingPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                if (lightingObj.userData.vertices && lightingObj.userData.vertices.length >= 2) {
                                                    lightingObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                    lightingObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                }
                                                lightingPos.needsUpdate = true;
                                            }
                                        });
                                    }

                                    // ✅ Eğer bu buat ise ve socket wire'ları varsa, onları da sürükle (ilk 2 vertex)
                                    if (obj.userData?.type === 'socketBuat' &&
                                        obj.userData?.socketWireMapIds && obj.userData.socketWireMapIds.length > 0) {

                                        // Ana hattın kenar yönünü hesapla (vertex gizmo için)
                                        const edgeDir = new THREE.Vector3().subVectors(vEnd, vStart).normalize();
                                        // Buat'ın hareket yönünü hesapla
                                        const movementDir = new THREE.Vector3().subVectors(newPos, socketData.originalPosition).normalize();

                                        obj.userData.socketWireMapIds.forEach(socketWireId => {
                                            // Socket wire'ı sahnede bul (cached)
                                            const socketObj = _findById(socketWireId);
                                            if (socketObj && socketObj.isLine &&
                                                (socketObj.userData?.function === 'socket' ||
                                                 socketObj.userData?.function === 'simpleSocket')) {
                                                const socketPos = socketObj.geometry.attributes.position;
                                                const vertexCount = socketPos.count;
                                                const firstVertex = new THREE.Vector3(socketPos.getX(0), socketPos.getY(0), socketPos.getZ(0));
                                                const secondVertex = new THREE.Vector3(socketPos.getX(1), socketPos.getY(1), socketPos.getZ(1));
                                                const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);
                                                socketPos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                if (vertexCount >= 3) {
                                                    const thirdVertex = new THREE.Vector3(socketPos.getX(2), socketPos.getY(2), socketPos.getZ(2));
                                                    const secondEdgeDir = new THREE.Vector3().subVectors(thirdVertex, secondVertex).normalize();
                                                    const dotProduct = Math.abs(movementDir.dot(secondEdgeDir));
                                                    if (dotProduct > 0.7) {
                                                        const newSecondVertex = newPos.clone().add(offset);
                                                        socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                        if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                            socketObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                        }
                                                    }
                                                } else {
                                                    const newSecondVertex = newPos.clone().add(offset);
                                                    socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                    if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                        socketObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                    }
                                                }
                                                if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 1) {
                                                    socketObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                }
                                                socketPos.needsUpdate = true;
                                            }
                                        });
                                    }
                                }
                            });
                        }
                    } else if (type === 'midpoint' && perpVector) {
                        // ✅ Orta nokta sürüklendiğinde kenarı PARALEL taşı
                        // Kenarların AÇILARI korunur, sadece BOYLARI değişir

                        const startIdx = originalEdgePositions.current.collinearStartIdx !== undefined
                            ? originalEdgePositions.current.collinearStartIdx
                            : edgeIndex;
                        const endIdx = originalEdgePositions.current.collinearEndIdx !== undefined
                            ? originalEdgePositions.current.collinearEndIdx
                            : edgeIndex;

                        const blockStart = originalEdgePositions.current.blockStart || originalEdgePositions.current.start;
                        const blockEnd = originalEdgePositions.current.blockEnd || originalEdgePositions.current.end;
                        const blockVertices = originalEdgePositions.current.blockVertices;

                        // Sürüklenen kenarın yeni pozisyonları (paralel taşınmış)
                        let newBlockStart = blockStart.clone().add(perpVector);
                        let newBlockEnd = blockEnd.clone().add(perpVector);

                        // 🔍 KOMŞU KENARLARIN AÇILARINI KORU

                        // Sol komşu kenar (startIdx-1 → startIdx)
                        if (startIdx > 0) {
                            const prevVertex = new THREE.Vector3(
                                pos.getX(startIdx - 1),
                                pos.getY(startIdx - 1),
                                pos.getZ(startIdx - 1)
                            );
                            const prevDir = new THREE.Vector3().subVectors(blockStart, prevVertex).normalize();
                            const blockDir = new THREE.Vector3().subVectors(blockEnd, blockStart).normalize();

                            const intersection = rayIntersection2D(prevVertex, prevDir, newBlockStart, blockDir);
                            if (intersection) {
                                newBlockStart = intersection;
                            }

                            // NOT: Panel çıkış wire'ı V0-V1 kısıtı perpVector hesaplama
                            // aşamasında uygulanıyor (lastValidMidpointPerp ile). Burada
                            // ek kontrol gereksiz — perpVector zaten son geçerli sınırın
                            // ötesine taşmaz, gizmo da onunla senkron kilitlenir.
                        }

                        // Sağ komşu kenar (endIdx+1 → endIdx+2)
                        if (endIdx + 1 < pos.count - 1) {
                            const nextVertex = new THREE.Vector3(
                                pos.getX(endIdx + 2),
                                pos.getY(endIdx + 2),
                                pos.getZ(endIdx + 2)
                            );
                            const nextDir = new THREE.Vector3().subVectors(blockEnd, nextVertex).normalize();
                            const blockDirReversed = new THREE.Vector3().subVectors(blockStart, blockEnd).normalize();

                            const intersection = rayIntersection2D(nextVertex, nextDir, newBlockEnd, blockDirReversed);
                            if (intersection) {
                                newBlockEnd = intersection;
                            }
                        }

                        // Update positions of all vertices in the block
                        const totalLengthOriginal = blockStart.distanceTo(blockEnd);

                        for (let i = startIdx; i <= endIdx + 1; i++) {
                            if (i === startIdx) {
                                pos.setXYZ(i, newBlockStart.x, newBlockStart.y, newBlockStart.z);
                            } else if (i === endIdx + 1) {
                                pos.setXYZ(i, newBlockEnd.x, newBlockEnd.y, newBlockEnd.z);
                            } else {
                                // Internal vertex - interpolate
                                if (blockVertices && blockVertices.length > 0) {
                                    const originalV = blockVertices[i - startIdx];
                                    const distFromStart = originalV.distanceTo(blockStart);
                                    const t = totalLengthOriginal > 0.001 ? distFromStart / totalLengthOriginal : 0;

                                    const vNew = new THREE.Vector3().lerpVectors(newBlockStart, newBlockEnd, t);
                                    pos.setXYZ(i, vNew.x, vNew.y, vNew.z);
                                }
                            }
                        }

                        // Update Components
                        if (originalSockets.current && originalSockets.current.length > 0) {
                            originalSockets.current.forEach(socketData => {
                                // Find which edge this socket belongs to
                                const edgeIdx = socketData.edgeIndex;

                                // Get new start/end of this edge from updated pos buffer
                                const s = new THREE.Vector3(pos.getX(edgeIdx), pos.getY(edgeIdx), pos.getZ(edgeIdx));
                                const e = new THREE.Vector3(pos.getX(edgeIdx + 1), pos.getY(edgeIdx + 1), pos.getZ(edgeIdx + 1));

                                if (socketData.isWireMarker) {
                                    // Wire marker: updateGizmos'da güncellenir
                                    // But we can update position here too for smoothness
                                    const newPos = new THREE.Vector3().lerpVectors(s, e, 0.5);
                                    socketData.object.position.copy(newPos);

                                    // Rotation
                                    const edgeDir = new THREE.Vector3().subVectors(e, s);
                                    const angle = Math.atan2(edgeDir.y, edgeDir.x);
                                    const angleRad = THREE.MathUtils.degToRad(45);
                                    socketData.object.rotation.z = angle + angleRad;
                                } else {
                                    const newPos = new THREE.Vector3().lerpVectors(s, e, socketData.t);
                                    socketData.object.position.copy(newPos);

                                    // ✅ Eğer bu buat ise ve fixture wire'ları varsa, onları da sürükle
                                    const obj = socketData.object;
                                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                        obj.userData?.fixtureWireMapIds && obj.userData.fixtureWireMapIds.length > 0) {

                                        obj.userData.fixtureWireMapIds.forEach(fixtureWireId => {
                                            // Fixture wire'ı sahnede bul (cached)
                                            const fixtureObj = _findById(fixtureWireId);
                                            if (fixtureObj && fixtureObj.isLine &&
                                                fixtureObj.userData?.function === 'wireLightingFixture') {
                                                const fixturePos = fixtureObj.geometry.attributes.position;
                                                fixturePos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                if (fixtureObj.userData.vertices && fixtureObj.userData.vertices.length > 0) {
                                                    fixtureObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                    const controlPoints = fixtureObj.userData.vertices.map(v =>
                                                        new THREE.Vector3(v.x, v.y, v.z || 0)
                                                    );
                                                    const curve = new THREE.CatmullRomCurve3(controlPoints);
                                                    const divisions = Math.max(10, controlPoints.length * 10);
                                                    const splinePoints = curve.getPoints(divisions);
                                                    fixtureObj.geometry.dispose();
                                                    fixtureObj.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
                                                }
                                                fixturePos.needsUpdate = true;
                                            }
                                        });
                                    }

                                    // ✅ Eğer bu buat ise ve lighting wire'ları varsa, onları da sürükle (ilk 2 vertex)
                                    if ((obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') &&
                                        obj.userData?.lightingWireMapIds && obj.userData.lightingWireMapIds.length > 0) {

                                        obj.userData.lightingWireMapIds.forEach(lightingWireId => {
                                            // Lighting wire'ı sahnede bul
                                            const lightingObj = _findById(lightingWireId);
                                            if (lightingObj && lightingObj.isLine &&
                                                (lightingObj.userData?.function === 'wireLighting' ||
                                                 lightingObj.userData?.function === 'simpleLighting')) {
                                                const lightingPos = lightingObj.geometry.attributes.position;
                                                const firstVertex = new THREE.Vector3(lightingPos.getX(0), lightingPos.getY(0), lightingPos.getZ(0));
                                                const secondVertex = new THREE.Vector3(lightingPos.getX(1), lightingPos.getY(1), lightingPos.getZ(1));
                                                const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);
                                                lightingPos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                const newSecondVertex = newPos.clone().add(offset);
                                                lightingPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                if (lightingObj.userData.vertices && lightingObj.userData.vertices.length >= 2) {
                                                    lightingObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                    lightingObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                }
                                                lightingPos.needsUpdate = true;
                                            }
                                        });
                                    }

                                    // ✅ Eğer bu buat ise ve socket wire'ları varsa, onları da sürükle (ilk 2 vertex)
                                    if (obj.userData?.type === 'socketBuat' &&
                                        obj.userData?.socketWireMapIds && obj.userData.socketWireMapIds.length > 0) {

                                        // Ana hattın kenar yönünü hesapla (midpoint paralel için)
                                        const edgeDir = new THREE.Vector3().subVectors(e, s).normalize();
                                        // Buat'ın hareket yönü (perpVector - midpoint paralel sürükleme)
                                        const movementDir = perpVector.clone().normalize();

                                        obj.userData.socketWireMapIds.forEach(socketWireId => {
                                            // Socket wire'ı sahnede bul (cached)
                                            const socketObj = _findById(socketWireId);
                                            if (socketObj && socketObj.isLine &&
                                                (socketObj.userData?.function === 'socket' ||
                                                 socketObj.userData?.function === 'simpleSocket')) {
                                                const socketPos = socketObj.geometry.attributes.position;
                                                const vertexCount = socketPos.count;
                                                const firstVertex = new THREE.Vector3(socketPos.getX(0), socketPos.getY(0), socketPos.getZ(0));
                                                const secondVertex = new THREE.Vector3(socketPos.getX(1), socketPos.getY(1), socketPos.getZ(1));
                                                const offset = new THREE.Vector3().subVectors(secondVertex, firstVertex);
                                                socketPos.setXYZ(0, newPos.x, newPos.y, newPos.z);
                                                if (vertexCount >= 3) {
                                                    const thirdVertex = new THREE.Vector3(socketPos.getX(2), socketPos.getY(2), socketPos.getZ(2));
                                                    const secondEdgeDir = new THREE.Vector3().subVectors(thirdVertex, secondVertex).normalize();
                                                    const dotProduct = Math.abs(movementDir.dot(secondEdgeDir));
                                                    if (dotProduct > 0.7) {
                                                        const newSecondVertex = newPos.clone().add(offset);
                                                        socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                        if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                            socketObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                        }
                                                    }
                                                } else {
                                                    const newSecondVertex = newPos.clone().add(offset);
                                                    socketPos.setXYZ(1, newSecondVertex.x, newSecondVertex.y, newSecondVertex.z);
                                                    if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 2) {
                                                        socketObj.userData.vertices[1] = { x: newSecondVertex.x, y: newSecondVertex.y, z: newSecondVertex.z };
                                                    }
                                                }
                                                if (socketObj.userData.vertices && socketObj.userData.vertices.length >= 1) {
                                                    socketObj.userData.vertices[0] = { x: newPos.x, y: newPos.y, z: newPos.z };
                                                }
                                                socketPos.needsUpdate = true;
                                            }
                                        });
                                    }
                                }
                            });
                        }
                    } else if (type === 'center') {
                        for (let i = 0; i < pos.count; i++) {
                            const v = new THREE.Vector3().fromBufferAttribute(pos, i).add(delta);
                            pos.setXYZ(i, v.x, v.y, v.z);
                        }
                    } else if (type === 'start') {
                        const v = new THREE.Vector3(positions[0], positions[1], positions[2]).add(delta);
                        pos.setXYZ(0, v.x, v.y, v.z);
                    } else if (type === 'end') {
                        const last = pos.count - 1;
                        const v = new THREE.Vector3(
                            positions[last * 3],
                            positions[last * 3 + 1],
                            positions[last * 3 + 2]
                        ).add(delta);
                        pos.setXYZ(last, v.x, v.y, v.z);
                    }

                    pos.needsUpdate = true;
                }
            });

            updateGizmos(scene, selectedGizmo.current);
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                // Eğer gizmo hareket ettiriliyorsa, onu iptal et
                if (isMoving.current && selectedGizmo.current) {
                    const gizmo = selectedGizmo.current;

                    gizmo.parent ? gizmo.parent.remove(gizmo) : scene.remove(gizmo);
                    gizmo.geometry?.dispose();
                    gizmo.material?.dispose();

                    const { type, lineId, vertexIndex, edgeIndex } = gizmo.userData;

                    // ===== BLOCK DRAGGING ESC RESTORE =====
                    if (isBlockDragging.current && blockObjRef.current) {
                        if (blockIsOCS.current) {
                            const m = blockObjRef.current.matrix.elements;
                            m[12] = originalBlockPos.current.x;
                            m[13] = originalBlockPos.current.y;
                            m[14] = originalBlockPos.current.z;
                            blockObjRef.current.matrixWorldNeedsUpdate = true;
                        } else {
                            blockObjRef.current.position.copy(originalBlockPos.current);
                        }

                        // Cleanup
                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isBlockDragging.current = false;
                        blockObjRef.current = null;
                        blockIsOCS.current = false;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END BLOCK DRAGGING ESC =====

                    // ===== MTEXT MOVING ESC RESTORE =====
                    if (isMtextMoving.current && mtextObjRef.current) {
                        mtextObjRef.current.position.copy(originalMtextPos.current);

                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isMtextMoving.current = false;
                        mtextObjRef.current = null;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END MTEXT MOVING ESC =====

                    // ===== MTEXT RESIZING ESC RESTORE =====
                    if (isMtextResizing.current && mtextObjRef.current) {
                        mtextObjRef.current.position.copy(originalMtextPos.current);
                        mtextObjRef.current.scale.copy(originalMtextScale.current);

                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isMtextResizing.current = false;
                        mtextObjRef.current = null;
                        mtextCornerIdx.current = -1;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END MTEXT RESIZING ESC =====

                    // ===== SIMPLE DRAWING ESC RESTORE =====
                    if (isSimpleDrawing.current) {
                        // Restore all vertices
                        scene.traverse(obj => {
                            if (obj.isLine && obj.userData.id === lineId) {
                                const pos = obj.geometry.attributes.position;
                                for (let i = 0; i < originalAllVertices.current.length; i++) {
                                    const v = originalAllVertices.current[i];
                                    pos.setXYZ(i, v.x, v.y, v.z);
                                }
                                pos.needsUpdate = true;
                                obj.geometry.computeBoundingSphere();
                            }
                        });

                        // Cleanup
                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isSimpleDrawing.current = false;
                        originalAllVertices.current = [];
                        removeAllSnapMarkers(scene);
                        return; // Skip wire-specific restore
                    }
                    // ===== END SIMPLE DRAWING ESC =====

                    // ===== CIRCLE DRAWING ESC RESTORE =====
                    if (isCircleDrawing.current && originalCircleData.current) {
                        const circleId = gizmo.userData.circleId;
                        scene.traverse(obj => {
                            if (obj.isLine && obj.userData.id === circleId) {
                                const pos = obj.geometry.attributes.position;
                                for (let i = 0; i < originalCircleData.current.vertices.length; i++) {
                                    const v = originalCircleData.current.vertices[i];
                                    pos.setXYZ(i, v.x, v.y, v.z);
                                }
                                pos.needsUpdate = true;
                                obj.geometry.computeBoundingSphere();
                                // userData'yı orijinale döndür
                                obj.userData.center = { ...originalCircleData.current.center };
                                obj.userData.radius = originalCircleData.current.radius;
                            }
                        });

                        // Cleanup
                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isCircleDrawing.current = false;
                        originalCircleData.current = null;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END CIRCLE DRAWING ESC =====

                    // ===== ARC DRAWING ESC RESTORE =====
                    if (isArcDrawing.current && originalArcData.current) {
                        const arcId = gizmo.userData.arcId;
                        scene.traverse(obj => {
                            if (obj.isLine && obj.userData.id === arcId) {
                                const pos = obj.geometry.attributes.position;
                                for (let i = 0; i < originalArcData.current.vertices.length; i++) {
                                    const v = originalArcData.current.vertices[i];
                                    pos.setXYZ(i, v.x, v.y, v.z);
                                }
                                pos.needsUpdate = true;
                                obj.geometry.computeBoundingSphere();
                                obj.userData.center = { ...originalArcData.current.center };
                                obj.userData.radius = originalArcData.current.radius;
                                obj.userData.startAngle = originalArcData.current.startAngle;
                                obj.userData.endAngle = originalArcData.current.endAngle;
                            }
                        });
                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isArcDrawing.current = false;
                        originalArcData.current = null;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END ARC DRAWING ESC =====

                    // ===== ELLIPSE DRAWING ESC RESTORE =====
                    if (isEllipseDrawing.current && originalEllipseData.current) {
                        const ellipseId = gizmo.userData.ellipseId;
                        scene.traverse(obj => {
                            if (obj.isLine && obj.userData.id === ellipseId) {
                                const pos = obj.geometry.attributes.position;
                                for (let i = 0; i < originalEllipseData.current.vertices.length; i++) {
                                    const v = originalEllipseData.current.vertices[i];
                                    pos.setXYZ(i, v.x, v.y, v.z);
                                }
                                pos.needsUpdate = true;
                                obj.geometry.computeBoundingSphere();
                                obj.userData.center = { ...originalEllipseData.current.center };
                                obj.userData.radiusX = originalEllipseData.current.radiusX;
                                obj.userData.radiusY = originalEllipseData.current.radiusY;
                                obj.userData.rotation = originalEllipseData.current.rotation;
                            }
                        });
                        selectedGizmo.current = null;
                        isMoving.current = false;
                        isEllipseDrawing.current = false;
                        originalEllipseData.current = null;
                        removeAllSnapMarkers(scene);
                        return;
                    }
                    // ===== END ELLIPSE DRAWING ESC =====

                    scene.traverse(obj => {
                        if (obj.isLine && obj.userData.id === lineId) {
                            const pos = obj.geometry.attributes.position;

                            if (type === 'vertex') {
                                // ✅ Vertex ve tüm etkilenen noktaları orijinal pozisyonlarına döndür
                                const data = originalVertexData.current;
                                if (data) {
                                    pos.setXYZ(vertexIndex, data.vertex.x, data.vertex.y, data.vertex.z);
                                    if (data.leftNeighbor) {
                                        pos.setXYZ(vertexIndex - 1, data.leftNeighbor.x, data.leftNeighbor.y, data.leftNeighbor.z);
                                    }
                                    if (data.rightNeighbor) {
                                        pos.setXYZ(vertexIndex + 1, data.rightNeighbor.x, data.rightNeighbor.y, data.rightNeighbor.z);
                                    }
                                }

                                // ✅ Komponentleri de geri al
                                if (originalSockets.current && originalSockets.current.length > 0) {
                                    originalSockets.current.forEach(socketData => {
                                        socketData.object.position.copy(socketData.originalPosition);
                                    });
                                }

                                // ✅ Bağlı lighting ve fixture wire'ları restore et (orijinal pozisyonlardan)
                                if (originalConnectedWires.current && originalConnectedWires.current.length > 0) {
                                    originalConnectedWires.current.forEach(wireData => {
                                        scene.traverse(wireObj => {
                                            if (wireObj.isLine && wireObj.userData?.id === wireData.wireId) {
                                                const wirePos = wireObj.geometry.attributes.position;

                                                if (wireData.type === 'fixture') {
                                                    // ✅ Fixture wire: sadece ilk vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length > 0) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };

                                                        // ✅ Spline'ı yeniden oluştur
                                                        const controlPoints = wireObj.userData.vertices.map(v =>
                                                            new THREE.Vector3(v.x, v.y, v.z || 0)
                                                        );
                                                        const curve = new THREE.CatmullRomCurve3(controlPoints);
                                                        const divisions = Math.max(10, controlPoints.length * 10);
                                                        const splinePoints = curve.getPoints(divisions);

                                                        wireObj.geometry.dispose();
                                                        wireObj.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
                                                    }
                                                } else if (wireData.type === 'lighting') {
                                                    // ✅ Lighting wire: ilk 2 vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);
                                                    wirePos.setXYZ(1, wireData.vertex1.x, wireData.vertex1.y, wireData.vertex1.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length >= 2) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };
                                                        wireObj.userData.vertices[1] = {
                                                            x: wireData.vertex1.x,
                                                            y: wireData.vertex1.y,
                                                            z: wireData.vertex1.z
                                                        };
                                                    }
                                                } else if (wireData.type === 'socket') {
                                                    // ✅ Socket wire: ilk 2 vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);
                                                    wirePos.setXYZ(1, wireData.vertex1.x, wireData.vertex1.y, wireData.vertex1.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length >= 2) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };
                                                        wireObj.userData.vertices[1] = {
                                                            x: wireData.vertex1.x,
                                                            y: wireData.vertex1.y,
                                                            z: wireData.vertex1.z
                                                        };
                                                    }
                                                }

                                                wirePos.needsUpdate = true;
                                            }
                                        });
                                    });
                                }
                            } else if (type === 'midpoint') {
                                // ✅ Midpoint iptal edildiğinde tüm blok vertexlerini restore et
                                const startIdx = originalEdgePositions.current.collinearStartIdx !== undefined
                                    ? originalEdgePositions.current.collinearStartIdx
                                    : edgeIndex;
                                const endIdx = originalEdgePositions.current.collinearEndIdx !== undefined
                                    ? originalEdgePositions.current.collinearEndIdx
                                    : edgeIndex;
                                const blockVertices = originalEdgePositions.current.blockVertices;

                                if (blockVertices && blockVertices.length > 0) {
                                    for (let i = startIdx; i <= endIdx + 1; i++) {
                                        const v = blockVertices[i - startIdx];
                                        pos.setXYZ(i, v.x, v.y, v.z);
                                    }
                                } else {
                                    pos.setXYZ(edgeIndex,
                                        originalEdgePositions.current.start.x,
                                        originalEdgePositions.current.start.y,
                                        originalEdgePositions.current.start.z
                                    );
                                    pos.setXYZ(edgeIndex + 1,
                                        originalEdgePositions.current.end.x,
                                        originalEdgePositions.current.end.y,
                                        originalEdgePositions.current.end.z
                                    );
                                }

                                // ✅ Socketleri de geri al
                                if (originalSockets.current && originalSockets.current.length > 0) {
                                    originalSockets.current.forEach(socketData => {
                                        socketData.object.position.copy(socketData.originalPosition);
                                    });
                                }

                                // ✅ Bağlı lighting ve fixture wire'ları restore et (orijinal pozisyonlardan)
                                if (originalConnectedWires.current && originalConnectedWires.current.length > 0) {
                                    originalConnectedWires.current.forEach(wireData => {
                                        scene.traverse(wireObj => {
                                            if (wireObj.isLine && wireObj.userData?.id === wireData.wireId) {
                                                const wirePos = wireObj.geometry.attributes.position;

                                                if (wireData.type === 'fixture') {
                                                    // ✅ Fixture wire: sadece ilk vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length > 0) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };

                                                        // ✅ Spline'ı yeniden oluştur
                                                        const controlPoints = wireObj.userData.vertices.map(v =>
                                                            new THREE.Vector3(v.x, v.y, v.z || 0)
                                                        );
                                                        const curve = new THREE.CatmullRomCurve3(controlPoints);
                                                        const divisions = Math.max(10, controlPoints.length * 10);
                                                        const splinePoints = curve.getPoints(divisions);

                                                        wireObj.geometry.dispose();
                                                        wireObj.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
                                                    }
                                                } else if (wireData.type === 'lighting') {
                                                    // ✅ Lighting wire: ilk 2 vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);
                                                    wirePos.setXYZ(1, wireData.vertex1.x, wireData.vertex1.y, wireData.vertex1.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length >= 2) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };
                                                        wireObj.userData.vertices[1] = {
                                                            x: wireData.vertex1.x,
                                                            y: wireData.vertex1.y,
                                                            z: wireData.vertex1.z
                                                        };
                                                    }
                                                } else if (wireData.type === 'socket') {
                                                    // ✅ Socket wire: ilk 2 vertex'i restore et
                                                    wirePos.setXYZ(0, wireData.vertex0.x, wireData.vertex0.y, wireData.vertex0.z);
                                                    wirePos.setXYZ(1, wireData.vertex1.x, wireData.vertex1.y, wireData.vertex1.z);

                                                    // ✅ userData vertices'i de güncelle
                                                    if (wireObj.userData.vertices && wireObj.userData.vertices.length >= 2) {
                                                        wireObj.userData.vertices[0] = {
                                                            x: wireData.vertex0.x,
                                                            y: wireData.vertex0.y,
                                                            z: wireData.vertex0.z
                                                        };
                                                        wireObj.userData.vertices[1] = {
                                                            x: wireData.vertex1.x,
                                                            y: wireData.vertex1.y,
                                                            z: wireData.vertex1.z
                                                        };
                                                    }
                                                }

                                                wirePos.needsUpdate = true;
                                            }
                                        });
                                    });
                                }
                            } else if (type === 'center') {
                                const delta = new THREE.Vector3().subVectors(originalPosition.current, gizmo.position);
                                for (let i = 0; i < pos.count; i++) {
                                    const v = new THREE.Vector3().fromBufferAttribute(pos, i).add(delta);
                                    pos.setXYZ(i, v.x, v.y, v.z);
                                }
                            } else if (type === 'start') {
                                pos.setXYZ(0, originalPosition.current.x, originalPosition.current.y, originalPosition.current.z);
                            } else if (type === 'end') {
                                const last = pos.count - 1;
                                pos.setXYZ(last, originalPosition.current.x, originalPosition.current.y, originalPosition.current.z);
                            }

                            pos.needsUpdate = true;
                        }
                    });

                    // ✅ Wire marker geometrilerini restore et
                    if (originalWireMarkerData.current && originalWireMarkerData.current.length > 0) {
                        originalWireMarkerData.current.forEach(markerData => {
                            const { group, childGeometries } = markerData;

                            // Her child line'ın geometrisini restore et
                            childGeometries.forEach(geomData => {
                                const { childIndex, positions } = geomData;
                                const child = group.children[childIndex];

                                if (child && child.isLine && child.geometry) {
                                    const pos = child.geometry.attributes.position;
                                    positions.forEach((vec, i) => {
                                        pos.setXYZ(i, vec.x, vec.y, vec.z);
                                    });
                                    pos.needsUpdate = true;
                                }
                            });
                        });
                        originalWireMarkerData.current = null;
                    }

                    selectedGizmo.current = null;
                    isMoving.current = false;
                }

                // 🔥 Snap marker varsa onu da her halükarda temizle
                removeAllSnapMarkers(scene);
            }

        };

        // Escape basılınca snap marker temizliği
        function removeAllSnapMarkers(scene) {

            const toRemove = [];

            scene.traverse(obj => {
                if (obj.name === '__snapMarker') {
                    toRemove.push(obj);
                }
            });
            toRemove.forEach(marker => {
                if (marker.parent) marker.parent.remove(marker);
                marker.geometry?.dispose();
                marker.material?.dispose();
            });
        }


        domElement.addEventListener("click", onClick);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            domElement.removeEventListener("click", onClick);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [scene, camera, domElement, floorPlans.length, wires.length, orthoMode, gridMode]);
}

